/**
 * Game.ts
 * Master game class. Owns the PixiJS application, the world/UI layers,
 * input, and the game loop.
 *
 * Stage 4: A* movement, group formation, Tempo system, right-click orders,
 *          move-target indicator.
 *
 * Scene graph:
 *   stage
 *     ├── world   (camera-transformed: map, units, indicators)
 *     └── ui      (screen-fixed: overlay, panel, selection box)
 */

import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { APP_RELEASE_STAMP } from './appMeta';
import { GameLoop }            from './engine/GameLoop';
import { AudioManager }        from './engine/AudioManager';
import { TileMap }             from './map/TileMap';
import { MapRenderer }         from './map/MapRenderer';
import { Camera }              from './map/Camera';
import { getMapSize, getMapSizeKey, isMobileDevice } from './mapConfig';
import { UnitRenderer }        from './rendering/UnitRenderer';
import { SelectionSystem }     from './systems/SelectionSystem';
import { CommandPanel, CommandButton } from './ui/CommandPanel';
import { MainMenu }            from './ui/MainMenu';
import { IntroScreen }        from './ui/IntroScreen';
import { OnboardingSystem }   from './ui/OnboardingSystem';
import { PauseMenu }           from './ui/PauseMenu';
import { ToastSystem }         from './ui/ToastSystem';
import { PathfindingSystem }   from './systems/PathfindingSystem';
import { RhythmSystem }        from './systems/RhythmSystem';
import { CombatSystem }        from './systems/CombatSystem';
import { WaveSystem }          from './systems/WaveSystem';
import { ResourceSystem }      from './systems/ResourceSystem';
import { FogSystem }           from './systems/FogSystem';
import { FogRenderer }         from './rendering/FogRenderer';
import { BuildSystem, type VenueNode, type VenueType } from './systems/BuildSystem';
import { VenueRenderer }       from './rendering/VenueRenderer';
import { BeaconSystem, type BeaconOwner } from './systems/BeaconSystem';
import { FutureBeacon }        from './buildings/FutureBeacon';
import { GearSystem }          from './systems/GearSystem';
import { GearNodeRenderer }    from './rendering/GearNodeRenderer';
import { PlacementSystem, BUILDING_DEFS } from './systems/PlacementSystem';
import { BuildingRenderer }    from './rendering/BuildingRenderer';
import { BuildMenu }           from './ui/BuildMenu';
import { activateSkill, canUseSkill, SKILL_NAMES } from './systems/SkillSystem';
import { SpellSystem, SPELLS } from './systems/SpellSystem';
import type { SpellKey } from './systems/SpellSystem';
import { EnemyAI }             from './ai/EnemyAI';
import { HUD }                 from './ui/HUD';
import { GameOverScreen }      from './ui/GameOverScreen';
import type { GameStats }      from './ui/GameOverScreen';
import { ContentRepository }   from './data/ContentRepository';
import { startBattleSession, trackBattleEvent, battleSessionId, flushUnitPositions, flushPendingPositions, type UnitPositionSample } from './data/Analytics';
import { SpatialIndex }        from './systems/SpatialIndex';
import { Unit }                from './entities/Unit';
import type { SpecialUnitRole } from './entities/Unit';
import { Faction } from './types';
import { FogState } from './types';
import type { FutureSong, GameContent, GridPosition, BandClass, EnemyType, SpawnEdge, BattleSnapshot } from './types';
import {
  CAMERA_PAN_SPEED,
  CAMERA_EDGE_SCROLL_MARGIN,
  CAMERA_ZOOM_STEP,
  ISO_TILE_WIDTH,
  ISO_TILE_HEIGHT,
  FAN_SPAWN_COUNT,
  FAN_HP,
  FAN_MOVE_SPEED,
  FAN_SIGHT_RADIUS,
  FAN_DAMAGE,
  FAN_ATTACK_RATE,
  FAN_ATTACK_RADIUS,
  UNIT_HIT_RADIUS,
  BEACON_NEAR_RADIUS,
  SONG_DROP_REP_BONUS,
  SONG_DROP_SALES_BONUS,
  SONG_DROP_GEAR_BONUS,
  SONG_DROP_ANTHEM_SECS,
  SONG_DROP_ANTHEM_MULT,
  SONG_DROP_STUN_SECS,
  SONG_DROP_SUSTAIN_TIME,
  VENUE_CLAIM_RANGE,
  VENUE_HP_MAX,
  SAVE_KEY,
  REP_PER_TILE_STEP,
  SKILL_TOURING_REP_THRESHOLD,
  SKILL_TOURING_REP_MULT,
  GOOD_BEACON_PULSE_MIN, // initial nextGoodBeaconPulse field value only
  CAPTURED_BEACON_PULSE_TIME,
  CAPTURED_BEACON_DAMAGE_RATIO,
  BADDIE_BEACON_ONLINE_GUARDS,
  BADDIE_BEACON_PULSE_GUARDS,
  SKILL_SPEED_BOOST_MULT,
  SPELL_TEMPORAL_ECHO_SLOW,
  CODEX_DECIDE_INTERVAL,
  GENZ_BURST_SPEED_MULT,
  GENZ_BURST_INTERVAL,
  MOGUL_AURA_INTERVAL,
  CORPORATE_HQ_HP,
  CORPORATE_HQ_SPAWN_INTERVAL,
  CORPORATE_HQ_DISCOVERY_RADIUS,
  CORPORATE_HQ_HIDDEN_ACTIVATION_WAVE,
  CORPORATE_HQ_EDGE_SPAWN_EXCLUSION_RADIUS,
  CORPORATE_HQ_CAMP_RADIUS,
  CORPORATE_HQ_SECURITY_INTERVAL,
  CORPORATE_HQ_HINT_PULSE_INTERVAL,
  CORPORATE_HQ_HINT_PULSE_WAVE,
  VENUE_UPGRADE_L2_REP,
  VENUE_UPGRADE_L2_SALES,
  VENUE_UPGRADE_L3_REP,
  VENUE_UPGRADE_L3_SALES,
  NIGEL_PROXIMITY_RADIUS,
  NIGEL_SIGNAL_BONUS,
  SONG_DROP_FANS_PER_VENUE,
  RIVAL_VENUE_BUILD_TIME,
  RIVAL_VENUE_HP,
  RIVAL_VENUE_MAX_ACTIVE,
  RIVAL_VENUE_MIN_BEACON_DISTANCE,
  RIVAL_VENUE_SITE_RADIUS_MAX,
  RIVAL_VENUE_SITE_RADIUS_MIN,
  RIVAL_VENUE_SPAWN_INTERVAL,
  RIVAL_VENUE_SPAWN_RADIUS,
  RIVAL_VENUE_LEVEL_2_TIME,
  RIVAL_VENUE_LEVEL_3_TIME,
  RIVAL_VENUE_LEVEL_HP_BONUS,
  RIVAL_VENUE_LEVEL_SPAWN_ACCEL,
  BADMANAGER_DETECTION_RADIUS,
  MUSIC_PRESS_REP_REWARD,
  MUSIC_PRESS_FAN_REWARD,
  MUSIC_PRESS_BAD_REVIEW_DURATION,
  MOGUL_SIGNAL_DRAIN,
  MOGUL_SALES_DRAIN,
  MOGUL_GLOBAL_DRAIN_CAP,
} from './constants';

/** Map fixed venue IDs to the BattlePlanner's expected building-type strings. */
function venueIdToBuildingType(id: string): string {
  const map: Record<string, string> = {
    dirty_duck:     'Pub',
    record_shop:    'MerchStall',
    community_hall: 'FlyerPost',
    indie_venue:    'Pub',
    rooftop_stage:  'FlyerPost',
    arts_centre:    'Pub',        // The Water Rats — sales+fan income
    underground:    'MerchStall', // The Snooker Club — rep+sales income
    town_hall:      'FlyerPost',  // Sin City — rep+fan income
  };
  return map[id] ?? 'Venue';
}

function nextRivalVenueEscalationTime(level: number): number {
  if (level <= 1) return RIVAL_VENUE_LEVEL_2_TIME;
  if (level === 2) return RIVAL_VENUE_LEVEL_3_TIME;
  return Infinity;
}

const SIGNAL_JAMMER_DRAIN = 0.35;
const SIGNAL_JAMMER_HEAL_DAMPENING = 0.22;
const SIGNAL_JAMMER_MAX_HEAL_DAMPENING = 0.65;
const BADDIE_BEACON_REPAIR_RATE = 6;
const BADDIE_BEACON_REPAIR_RANGE = 2.25;
const BADDIE_BEACON_REPAIR_CREWS = 1;
const BADDIE_BEACON_REPAIR_TOAST_COOLDOWN = 14;

// ── Save state ───────────────────────────────────────────────────────────────

interface SavedUnit {
  id?: string;
  col: number; row: number;
  faction: Faction;
  bandClass: string | null;
  enemyType: string | null;
  isHero: boolean;
  displayName: string;
  threatScale?: number;
  specialRole?: SpecialUnitRole | null;
  objectiveTarget?: GridPosition | null;
  constructionProgress?: number;
  constructionComplete?: boolean;
  isConstructing?: boolean;
  linkedUnitId?: string | null;
  spawnTimer?: number;
  rivalVenueLevel?: number;
  rivalVenueAge?: number;
  rivalVenueNextEscalation?: number;
  maxHp: number; hp: number;
  isDowned?: boolean;
  reviveProgress?: number;
  moveSpeed: number;
  attackDamage: number; attackRate: number;
  sightRadius: number; attackRadius: number;
  pressHostileTimer: number | null;
}

interface SavedVenue {
  id: string; name: string;
  venueType?: VenueType;
  col: number; row: number;
  buildingCol: number; buildingRow: number;
  state: string; progress: number; hp: number;
  repBonus: number; salesBonus: number; fanBonus: number; gearBonus: number;
  claimCostRep: number; claimCostSales: number;
}

interface SaveState {
  version: 1;
  savedAt: number;
  mapSeed: number;       // stored so the loaded game renders the same map
  mapWidth?: number;
  mapHeight?: number;
  elapsed: number;
  enemiesDefeated: number;
  songDropCount: number;
  goodBeaconPulseTimer?: number;
  nextGoodBeaconPulse?: number;
  baddieBeaconPulseTimer?: number;
  capturedBeaconPulseTimer?: number;
  corporateHQSpawnTimer?: number;
  corporateHQSecurityTimer?: number;
  corporateHQHintPulseTimer?: number;
  corporateHQDiscovered?: boolean;
  corporateHQDestroyed?: boolean;
  centralBeaconOwner?: BeaconOwner;
  centralBeaconCaptureTimer?: number;
  resources: {
    reputation: number; fanbase: number; fanbaseCap: number; sales: number; gear: number;
    peakReputation?: number; peakFanbase?: number; sceneScore?: number;
    badReviewRemaining?: number;
  };
  beaconSignal: number;
  waves: ReturnType<import('./systems/WaveSystem').WaveSystem['getState']>;
  spells?: ReturnType<import('./systems/SpellSystem').SpellSystem['getState']>;
  units: SavedUnit[];
  venues: SavedVenue[];
  buildSites: Array<{ id: string; gearProgress: number; completed: boolean; }>;
}

// IDs of the four pre-placed venues (don't need full reconstruction on load)
const ORIGINAL_VENUE_IDS = new Set([
  'dirty_duck', 'record_shop', 'community_hall', 'indie_venue',
  'rooftop_stage', 'arts_centre', 'underground', 'town_hall',
]);

// ── Band member spawn definitions ────────────────────────────────────────────
const BAND_SPAWNS: Array<{
  col: number; row: number;
  bandClass: 'protector' | 'promoter' | 'engine' | 'sustain' | 'mystic' | 'songwriter';
  name: string;
  hpKey:    'unitHpProtector' | 'unitHpPromoter' | 'unitHpEngine' | 'unitHpSustain' | 'unitHpMystic' | 'unitHpSongwriter';
  speedKey: 'unitMoveSpeed'   | 'unitMoveSpeedPromoter' | 'unitMoveSpeedMystic' | 'unitMoveSpeedSongwriter';
}> = [
  { col: 34, row: 30, bandClass: 'protector',  name: 'Steve & The Guitar Gang',    hpKey: 'unitHpProtector',  speedKey: 'unitMoveSpeed' },
  { col: 42, row: 26, bandClass: 'promoter',   name: 'Ali & The Vocal Mob',         hpKey: 'unitHpPromoter',   speedKey: 'unitMoveSpeedPromoter' },
  { col: 34, row: 26, bandClass: 'engine',     name: 'Chris & The Rhythm Crew',     hpKey: 'unitHpEngine',     speedKey: 'unitMoveSpeed' },
  { col: 42, row: 30, bandClass: 'sustain',    name: 'Alex & The Bass Line',        hpKey: 'unitHpSustain',    speedKey: 'unitMoveSpeed' },
  { col: 38, row: 34, bandClass: 'mystic',     name: 'Jim & The Keyboard Prophets', hpKey: 'unitHpMystic',     speedKey: 'unitMoveSpeedMystic' },
  { col: 38, row: 22, bandClass: 'songwriter', name: 'Nigel & The Song Catchers',   hpKey: 'unitHpSongwriter', speedKey: 'unitMoveSpeedSongwriter' },
];

// ── Move-target indicator ────────────────────────────────────────────────────
interface MoveIndicator {
  g:        Graphics;
  elapsed:  number;
  lifetime: number;
  colour:   number;
}

interface TorrentShot {
  g:        Graphics;
  from:     { x: number; y: number };
  to:       { x: number; y: number };
  elapsed:  number;
  lifetime: number;
}

const INDICATOR_LIFETIME = 0.65; // seconds
const TORRENT_SHOT_LIFETIME = 0.42; // seconds
const HW = ISO_TILE_WIDTH  / 2;
const HH = ISO_TILE_HEIGHT / 2;

export class Game {
  private readonly app: Application;
  private readonly world = new Container();
  private readonly ui    = new Container();

  private map!: TileMap;
  private mapRenderer!: MapRenderer;
  private camera!: Camera;
  private loop!: GameLoop;
  private readonly audio = new AudioManager();
  private content!: GameContent;

  // Game state machine
  private gameState: 'menu' | 'playing' | 'paused' = 'menu';

  /**
   * Spatial index rebuilt once per tick (after unit movement, before combat).
   * Shared between CombatSystem and the separation system.
   */
  private readonly spatial = new SpatialIndex();

  // Toast change-detection state
  private prevWave     = 0;
  private prevBand     = 'normal';
  private prevSongDrop = false;
  private prevBeaconOwner: BeaconOwner = 'player';
  private prevSceneTier = 'local';

  // Stage 10 stats
  private enemiesDefeated = 0;
  private songDropCount   = 0;
  /** Composite score of the player's build investment; drives the late-game enemy pressure curve. */
  private buildScore      = 0;
  /** Countdown (s) between Nigel's idle beacon-return checks. */
  private nigelIdleTimer  = 10;
  private goodBeaconPulseTimer = 0;
  private nextGoodBeaconPulse = GOOD_BEACON_PULSE_MIN;
  private baddieBeaconPulseTimer = 0;
  private capturedBeaconPulseTimer = 0;
  private corporateHQSpawnTimer = 0;
  private corporateHQSecurityTimer = 0;
  private corporateHQHintPulseTimer = 0;
  private corporateHQDiscovered = false;
  private corporateHQDestroyed = false;
  private baddieBeaconRepairToastCooldown = 0;
  private positionSampleTimer = 0;
  private readonly positionSamples: UnitPositionSample[] = [];
  private futureSongBag: FutureSong[] = [];
  private nextFutureSong: FutureSong | null = null;
  private analyticsSnapshotTimer = 0;
  private enemyBeaconControlTime = 0;
  private readonly reportedDownedUnits = new Set<string>();

  private readonly units: Unit[] = [];
  private unitRenderer!: UnitRenderer;
  private selection!: SelectionSystem;
  private commandPanel!: CommandPanel;
  private mainMenu!: MainMenu;
  private introScreen!: IntroScreen;
  private onboarding!: OnboardingSystem;
  private pauseMenu!: PauseMenu;
  private toasts!: ToastSystem;
  private gameOverScreen!: GameOverScreen;
  private pathfinding!: PathfindingSystem;
  private rhythm!: RhythmSystem;
  private combat!: CombatSystem;
  private waves!: WaveSystem;
  private resources!: ResourceSystem;
  private hud!: HUD;
  private fog!: FogSystem;
  private fogRenderer!: FogRenderer;
  private build!: BuildSystem;
  private venueRenderer!: VenueRenderer;
  private beacon!: FutureBeacon;
  private beaconSystem!: BeaconSystem;
  private gearSystem!: GearSystem;
  private gearNodeRenderer!: GearNodeRenderer;
  private placement!:        PlacementSystem;
  private buildingRenderer!: BuildingRenderer;
  private buildMenu!:        BuildMenu;
  private spells!: SpellSystem;
  private jimWasDown = false;
  private enemyAI!: EnemyAI;
  private codexTimer    = 0;
  private aiThrottle    = 0; // mobile AI frame accumulator — runs AI at ~10 fps
  // Track last camera state so fog rebuilds whenever the viewport moves/zooms.
  private lastCamX = 0;
  private lastCamY = 0;
  private lastCamZ = 0;
  private moveIndicators: MoveIndicator[] = [];
  private torrentShots: TorrentShot[] = [];

  // Input state
  private readonly keys    = new Set<string>();
  private readonly pointer = { x: 0, y: 0, inside: false };

  // Middle-button pan
  private mbDragging = false;

  // Right-button: click = move order, drag = pan
  private rbDown                                    = false;
  private rbDragStart: { sx: number; sy: number }   | null = null;
  private rbMoved                                   = false;
  private rbDragging                                = false;
  private attackCommandArmed                        = false;

  // Left-button: click = select, drag = box-select
  private lbDown                                    = false;
  private lbDragStart: { sx: number; sy: number }   | null = null;
  private lbMoved                                   = false;
  private selBoxGfx!: Graphics;

  // Touch gestures: single-finger pan, two-finger pinch-zoom + pan
  private readonly touchPointers = new Map<number, { x: number; y: number }>();
  private touchPinchDist  = 0;
  private touchMidpoint   = { x: 0, y: 0 };
  private touchMoved      = false;

  private hoverTile: GridPosition | null = null;

  // Frame state
  private elapsed       = 0;
  private fps           = 0;
  private nudgeTimer    = 0; // seconds since last stacked-unit separation pass

  // Cinematic zoom-out played once when the player first enters the game.
  private cinemaZoom: { elapsed: number; duration: number; startZoom: number; targetZoom: number } | null = null;

  // UI
  private overlay!: Text;
  private hint!: Text;
  private versionStamp!: Text;
  private aboutLink!: Text;
  private overlayBody!: Text;       // info lines that hide/show
  private overlayToggleBtn!: Text;  // [HIDE] / [SHOW] button
  private overlayAutoplayBtn!: Text;// [AUTOPLAY: OFF/ON] button
  private overlayVisible = false;
  private gameFooterVisible = true;

  // Autoplay (demo mode)
  private autoplayActive = false;
  private autoplayTick   = 0;
  private autoplayIntent = 'standing by';
  private readonly AUTOPLAY_INTERVAL = 4.0; // seconds between AI decisions

  constructor() {
    // On mobile, clamp DPR to 1.5 — DPR 3 on modern phones triples pixel work
    // and VRAM for negligible visual benefit in an isometric pixel-art style.
    const dpr = isMobileDevice()
      ? Math.min(window.devicePixelRatio || 1, 1.5)
      : (window.devicePixelRatio || 1);
    this.app = new Application({
      background: '#0b0c0f',
      resizeTo: window,
      antialias: true,
      autoDensity: true,
      resolution: dpr,
    });

    document.body.appendChild(this.app.view as HTMLCanvasElement);
    this.app.stage.addChild(this.world, this.ui);
    window.addEventListener('resize', () => this.onResize());
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  async start(): Promise<void> {
    this.content = await ContentRepository.load();
    void this.audio.preload();
    // Seed the first good-beacon pulse interval from balance (field init used the constant)
    this.nextGoodBeaconPulse = this.content.balance.goodBeaconPulseMin;

    // Peek at any save for its map seed BEFORE constructing TileMap.
    // A loaded game re-uses the same seed so the map is identical to when it
    // was saved.  A new game gets a fresh random seed.
    // createAsync() runs map generation in a Web Worker so the main thread
    // stays responsive during the ~500ms terrain compute.
    const mapSeed = this.peekSaveMapSeed();
    performance.mark('fb:map-gen-start');
    this.map = await TileMap.createAsync(mapSeed);
    performance.mark('fb:map-gen-end');
    performance.measure('fb:map-gen', 'fb:map-gen-start', 'fb:map-gen-end');
    this.mapRenderer = new MapRenderer(this.map, this.world);
    this.mapRenderer.init();

    // FogRenderer must be added to world BEFORE UnitRenderer so it sits between
    // the terrain layer (index 0) and the unit layer (index 2). FogRenderer's
    // constructor calls addChildAt(g, 1) to slot itself in the right position.
    this.fog         = new FogSystem(this.map);
    this.fogRenderer = new FogRenderer(
      this.world, this.map,
      this.content.balance.fogExploredAlpha,
      this.content.balance.fogUnexploredAlpha,
    );

    this.camera = new Camera(this.world);
    this.camera.setZoomLimits(getMapSize().zoomMin);
    const b     = this.mapRenderer.bounds;
    this.camera.setBounds(b.x, b.y, b.width, b.height);
    this.camera.setViewport(this.app.screen.width, this.app.screen.height);
    this.fogRenderer.setScreenSize(this.app.screen.width, this.app.screen.height);
    this.camera.centreOn(this.map.beaconCenter());

    this.unitRenderer = new UnitRenderer(this.world, this.map);

    // Apply fog-of-war admin setting (persisted in localStorage by the admin panel).
    // When fog is OFF: leave fogCheck null so UnitRenderer's else-branch fires
    // (g.visible = true for all units). When fog is ON: wire up the real check.
    if (localStorage.getItem('fb_fog_enabled') === 'false') {
      this.fogRenderer.setVisible(false);
      // fogCheck stays null → UnitRenderer always shows all non-player units
    } else {
      this.unitRenderer.setFogCheck((c, r) => this.fog.getState(c, r));
    }

    this.selection    = new SelectionSystem(this.camera, this.map);
    this.commandPanel = new CommandPanel(this.ui, this.makeButtons());
    this.commandPanel.resize(this.app.screen.width, this.app.screen.height);

    this.pathfinding = new PathfindingSystem(this.map);
    this.rhythm      = new RhythmSystem({
      tempoDefault:   this.content.balance.tempoDefault,
      tempoDecayRate: this.content.balance.tempoDecayRate,
      tempoEnemyHit:  this.content.balance.tempoEnemyHit,
    });
    this.resources    = new ResourceSystem(this.content.balance);
    this.combat       = new CombatSystem(
      this.map,
      this.rhythm,
      this.pathfinding,
      this.resources,
      (from, to) => this.showTorrentShot(from, to),
      press => this.onMusicPressConverted(press),
      manager => this.onBadManagerDetected(manager),
    );
    this.waves        = new WaveSystem(this.map, this.unitRenderer, this.units, this.content.balance);
    this.build        = new BuildSystem(this.resources, this.content.balance, this.map.venueLayouts, this.map.seed, this.map.width, this.map.height);
    this.beacon       = new FutureBeacon();
    this.beaconSystem = new BeaconSystem(this.beacon, this.resources);
    this.spells       = new SpellSystem();
    this.enemyAI      = new EnemyAI();
    this.gearSystem       = new GearSystem(this.resources, Math.floor(this.map.width / 2), Math.floor(this.map.height / 2), this.map.width, this.map.height);
    this.hud          = new HUD(this.ui, this.resources);
    this.hud.setBeacon(this.beaconSystem);
    this.hud.resize(this.app.screen.width, this.app.screen.height);

    // ── UI overlays (must be added last so they sit on top) ──────────────────
    this.toasts = new ToastSystem(this.ui);
    this.toasts.resize(this.app.screen.width, this.app.screen.height);

    this.pauseMenu = new PauseMenu(
      this.ui,
      () => { this.gameState = 'playing'; this.pauseMenu.hide(); },
      () => {
        this.saveGame();
        this.toasts.show('✓ Game saved', 'success', 2.5);
      },
      () => { window.location.reload(); },
    );

    // ── Load save (if one exists) before showing the main menu ───────────────
    const loaded = this.loadGame();

    const startPlaying = () => {
      this.mainMenu.hide();
      this.gameState = 'playing';
      this.setGameFooterVisible(true);
    };

    // Onboarding deck — shown once after the story intro, persisted in localStorage.
    this.onboarding = new OnboardingSystem(this.ui);

    // Story intro — shown for new games only; Continue skips straight in.
    this.introScreen = new IntroScreen(this.ui, () => {
      startPlaying();
      this.startCinematicZoom();
      // Show onboarding on first run; toast fires after it completes.
      this.onboarding.show(
        this.app.screen.width,
        this.app.screen.height,
        () => this.toasts.show('♪ Good luck out there!', 'success', 2.5),
      );
      // If onboarding was already completed (returning player), show toast now.
      if (this.onboarding.isComplete) {
        this.toasts.show('♪ Good luck out there!', 'success', 2.5);
      }
    });

    this.mainMenu = new MainMenu(
      this.ui,
      () => {
        if (loaded) {
          // A save was loaded — wipe it and reload so every system starts
          // completely fresh (no stale wave / venue / beacon state).
          localStorage.removeItem(SAVE_KEY);
          window.location.reload();
        } else {
          // No save — show the story intro before the game begins.
          // Reset onboarding so the band-role tips always show on a new game.
          this.onboarding.reset();
          this.mainMenu.hide();
          this.setGameFooterVisible(false);
          this.introScreen.show(this.app.screen.width, this.app.screen.height);
        }
      },
      loaded
        ? () => {
            startPlaying();
            this.toasts.show('♪ Welcome back!', 'success', 2.5);
          }
        : null,
    );
    this.mainMenu.show(this.app.screen.width, this.app.screen.height);
    this.setGameFooterVisible(false);
    flushPendingPositions();
    startBattleSession();
    trackBattleEvent('session_started', {
      loaded,
      mapSeed: this.map.seed,
      mapCode: this.map.mapCode,
      mapWidth: this.map.width,
      mapHeight: this.map.height,
      mapArea: this.map.width * this.map.height,
    });

    this.gameOverScreen = new GameOverScreen(
      this.ui,
      () => { window.location.reload(); },
      () => { window.location.reload(); },
    );

    // World-space renderers (sit above the unit layer in the world container)
    this.venueRenderer    = new VenueRenderer(this.world, this.map);
    this.gearNodeRenderer = new GearNodeRenderer(this.world, this.map);
    this.gearNodeRenderer.init(this.gearSystem.nodes);

    // Stage 8: player-built buildings
    this.placement        = new PlacementSystem(this.resources, this.map);
    this.buildingRenderer = new BuildingRenderer(this.world, this.map);
    this.buildMenu        = new BuildMenu(this.ui);
    this.buildMenu.init((type) => this.placement.enterPlacementMode(type));
    this.buildMenu.resize(this.app.screen.width, this.app.screen.height);

    // Tell CombatSystem which venues enemies should attack
    this.combat.setVenues(this.build.venues);

    // Only spawn defaults when no save was loaded (loadGame handles unit creation)
    if (!loaded) {
      this.spawnBandMembers();
      this.spawnCorporateHQ();
    } else {
      this.ensureCorporateHQExists();
    }
    this.buildOverlay();
    this.bindInput();

    this.loop = new GameLoop(
      (delta) => this.update(delta),
      ()      => this.render(),
    );
    this.loop.start();
  }

  // ── Spawn ────────────────────────────────────────────────────────────────

  private spawnBandMembers(): void {
    const bal = this.content.balance;
    const spawnPositions = this.map.bandSpawnPositions;
    for (let i = 0; i < BAND_SPAWNS.length; i++) {
      const def  = BAND_SPAWNS[i];
      // Use the map-generated random position; fall back to hardcoded if missing
      const pos  = spawnPositions[i] ?? { col: def.col, row: def.row };
      const unit = new Unit();
      unit.gridPos      = { ...pos };
      unit.faction      = Faction.Player;
      unit.bandClass    = def.bandClass;
      unit.isHero       = true;
      unit.displayName  = def.name;
      unit.maxHp        = bal[def.hpKey];
      unit.hp           = bal[def.hpKey];
      unit.moveSpeed    = bal[def.speedKey];
      unit.attackDamage = this.content.balance.unitDamage;
      unit.attackRate   = this.content.balance.unitAttackRate;
      unit.sightRadius  = this.content.balance.unitSightRadius;
      unit.attackRadius = this.content.balance.unitAttackRadius;

      this.units.push(unit);
      this.unitRenderer.add(unit);
    }
    this.selection.setUnits(this.units);
  }

  /**
   * Spawn a single Fan unit near `pos`, spreading multiple fans across adjacent
   * passable tiles so they don't all stack on the same square.
   */
  private spawnFan(pos: GridPosition, index: number): void {
    // Spiral outward from the venue access tile to find a passable spawn spot
    const offsets: [number, number][] = [
      [0, 0], [1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [-1, 1], [1, -1], [-1, -1],
    ];
    let spawnPos = pos;
    for (const [dc, dr] of offsets.slice(index)) {
      const candidate = { col: pos.col + dc, row: pos.row + dr };
      if (this.map.inBounds(candidate.col, candidate.row)
          && this.map.isPassable(candidate)
          && !this.isTileOccupied(candidate)) {
        spawnPos = candidate;
        break;
      }
    }

    const unit          = new Unit();
    unit.gridPos        = { col: spawnPos.col, row: spawnPos.row };
    unit.faction        = Faction.Player;
    unit.bandClass      = 'fan';
    unit.isHero         = false;
    unit.displayName    = 'Fan';
    unit.maxHp          = FAN_HP;
    unit.hp             = FAN_HP;
    unit.moveSpeed      = FAN_MOVE_SPEED;
    unit.attackDamage   = FAN_DAMAGE;
    unit.attackRate     = FAN_ATTACK_RATE;
    unit.sightRadius    = FAN_SIGHT_RADIUS;
    unit.attackRadius   = FAN_ATTACK_RADIUS;

    this.units.push(unit);
    this.unitRenderer.add(unit);
    this.selection.setUnits(this.units);
  }

  // ── Loop ──────────────────────────────────────────────────────────────────

  private update(delta: number): void {
    this.elapsed += delta;
    if (delta > 0) this.fps = this.fps * 0.9 + (1 / delta) * 0.1;

    // Path budget + deferred queue flush — must be first so callers see a fresh budget.
    this.pathfinding?.beginFrame();

    // Toast and onboarding animations run regardless of game state
    this.toasts.update(delta);
    this.onboarding?.update(delta);

    // Game simulation is suspended when on the main menu or paused
    if (this.gameState !== 'playing') return;

    // Sample band-member positions every 5 s for movement path analytics
    this.positionSampleTimer += delta;
    if (this.positionSampleTimer >= 5) {
      this.positionSampleTimer = 0;
      const wave = this.waves.currentWaveIndex;
      const elapsedSec = Math.round(this.elapsed);
      for (const u of this.units) {
        if (u.enemyType !== null || !u.alive) continue;
        if (u.bandClass === null || u.bandClass === 'fan' || u.bandClass === 'source') continue;
        this.positionSamples.push({ unit_role: u.bandClass, col: u.gridPos.col, row: u.gridPos.row, elapsed_seconds: elapsedSec, wave });
      }
    }

    // Rhythm
    this.rhythm.update(delta);
    const tempo = this.rhythm.tempoModifier;

    // Spell system
    this.spells.update(delta);

    // Detect Jim going down → lock spells for 60s
    const jim = this.units.find(u => u.bandClass === 'mystic' && u.alive);
    const jimDown = !jim || jim.isDowned;
    if (jimDown && !this.jimWasDown) this.spells.onJimDown();
    this.jimWasDown = jimDown;

    // Cinematic zoom-out — runs for the first 3 s after entering the game.
    // Suppress all player camera input while it is active.
    this.updateCinematicZoom(delta);

    // Camera pan from keyboard + edge scroll (suppressed during cinematic)
    if (!this.cinemaZoom) {
      let dx = 0, dy = 0;
      if (this.keys.has('a') || this.keys.has('arrowleft'))  dx -= 1;
      if (this.keys.has('d') || this.keys.has('arrowright')) dx += 1;
      if (this.keys.has('w') || this.keys.has('arrowup'))    dy -= 1;
      if (this.keys.has('s') || this.keys.has('arrowdown'))  dy += 1;

      if (this.pointer.inside) {
        const m = CAMERA_EDGE_SCROLL_MARGIN;
        if (this.pointer.x < m)                                dx -= 1;
        else if (this.pointer.x > this.app.screen.width  - m) dx += 1;
        if (this.pointer.y < m)                                dy -= 1;
        else if (this.pointer.y > this.app.screen.height - m) dy += 1;
      }

      if (!this.mbDragging && !this.rbDragging && (dx !== 0 || dy !== 0)) {
        this.camera.pan(dx * CAMERA_PAN_SPEED * delta, dy * CAMERA_PAN_SPEED * delta);
      }
    }

    // Unit movement — delta is scaled by tempo modifier. Units may move next
    // to each other, but cannot finish a step on an occupied tile.
    // Anthem-buffed friendlies move faster; stunned enemies don't move at all.
    for (const unit of this.units) {
      // Decay status-effect timers (real-time delta, not tempo-scaled)
      if (unit.anthemBuff      > 0) unit.anthemBuff      = Math.max(0, unit.anthemBuff      - delta);
      if (unit.stunTimer       > 0) unit.stunTimer       = Math.max(0, unit.stunTimer       - delta);
      if (unit.skillCooldown   > 0) unit.skillCooldown   = Math.max(0, unit.skillCooldown   - delta);
      if (unit.speedBoostTimer > 0) unit.speedBoostTimer = Math.max(0, unit.speedBoostTimer - delta);
      if (unit.attackBoostTimer > 0) unit.attackBoostTimer = Math.max(0, unit.attackBoostTimer - delta);

      // Skip movement for stunned units
      if (unit.stunTimer > 0) continue;

      // Apply speed multipliers: anthem buff stacks multiplicatively with Crowd Hype skill boost.
      // Temporal Echo slow applies to enemy units only.
      const anthemMult = (unit.anthemBuff  > 0 && unit.faction === Faction.Player) ? SONG_DROP_ANTHEM_MULT  : 1;
      const skillMult  = (unit.speedBoostTimer > 0 && unit.faction === Faction.Player) ? SKILL_SPEED_BOOST_MULT : 1;
      // moveSlowTimer slows enemies (Temporal Echo spell) and player units (Mogul interference aura)
      const slowMult   = unit.moveSlowTimer > 0 ? SPELL_TEMPORAL_ECHO_SLOW : 1;
      // GenZSwarm burst sprint — periodic speed spike
      const burstMult  = (unit.enemyType === 'GenZSwarm' && unit.burstTimer > 0) ? GENZ_BURST_SPEED_MULT : 1;
      const speedMult  = anthemMult * skillMult * slowMult * burstMult;

      // Snapshot position before update so we can detect a completed tile step.
      const prevCol = unit.gridPos.col;
      const prevRow = unit.gridPos.row;

      // Snapshot the next tile in the path so that if the unit abandons it due
      // to a block we can immediately re-route around the blocker.
      const blockedCandidate = unit.path.length > 0 ? { ...unit.path[0] } : null;
      const hadPath          = unit.path.length > 0;

      // crossFactionPassable=true: opponents don't block each other's movement.
      // CombatSystem stops units when they enter attack range, so tile-level
      // exclusion across factions is both unnecessary and causes the shake bug.
      unit.update(delta * tempo * speedMult, (pos) => this.isTileOccupied(pos, unit, true));

      // If the unit just abandoned a blocked path and has a known destination,
      // immediately re-path around the blocking tile instead of sitting idle.
      // This eliminates the "bash and freeze" visual: rather than waiting for
      // the combat system's next tick to re-route, we detour right now.
      if (hadPath && unit.path.length === 0 && blockedCandidate && unit.orderTarget) {
        const alt = this.pathfinding.findPath(unit.gridPos, unit.orderTarget, [blockedCandidate]);
        if (alt.length > 0) unit.setPath(alt);
      }

      // Band members earn REP per tile step. Units touring far from the Beacon
      // earn SKILL_TOURING_REP_MULT × the base rate — incentivises map spread.
      if (
        unit.faction === Faction.Player &&
        unit.bandClass !== 'fan' &&
        unit.bandClass !== 'source' &&
        this.resources.canEarnAmbientReputation() &&
        (unit.gridPos.col !== prevCol || unit.gridPos.row !== prevRow)
      ) {
        const beaconDist = Math.hypot(
          unit.gridPos.col - this.map.beacon.col,
          unit.gridPos.row - this.map.beacon.row,
        );
        const repMult = beaconDist >= SKILL_TOURING_REP_THRESHOLD ? SKILL_TOURING_REP_MULT : 1;
        this.resources.add('reputation', REP_PER_TILE_STEP * repMult);
      }
    }

    // Four times per second: separate any same-faction units sharing a tile.
    // Runs on ALL alive units (not just idle ones) so simultaneous arrivals are
    // caught before either unit's path has been cleared.
    this.nudgeTimer += delta;
    if (this.nudgeTimer >= 0.25) {
      this.nudgeTimer = 0;
      this.separateStackedUnits();
    }

    // Rebuild the spatial index once, after unit movement and before combat so
    // target-acquisition and isOccupied checks see the freshest positions.
    this.spatial.rebuild(this.units);

    // Combat uses real time: tempo affects movement, but swings/revives should
    // not freeze when Chris gets knocked down and the groove collapses.
    this.combat.update(this.units, delta, this.spatial);
    this.recordPlayerBattleLosses();

    // Build score: reflects how much the player has invested — used to gently
    // scale late-game wave pressure after a long grace period.
    this.buildScore = this.computeBuildScore();

    // On mobile, throttle AI systems to ~10 fps to free up frame budget.
    // On desktop, run every frame (aiThrottle stays 0 → condition always true).
    const AI_TICK = isMobileDevice() ? 0.1 : 0;
    this.aiThrottle += delta;
    if (this.aiThrottle >= AI_TICK) {
      const aiDelta = this.aiThrottle;
      this.aiThrottle = 0;

      // Wave spawner (real-time seconds, not tempo-scaled)
      this.waves.update(aiDelta, this.resources.reputation, this.beaconSystem.prophecyActive, this.buildScore);

      // Codex BattlePlanner: decide enemy tactics every CODEX_DECIDE_INTERVAL seconds.
      // Only starts after the first wave to avoid pestering during the tutorial grace period.
      if (this.waves.currentWaveIndex > 0) {
        this.codexTimer -= aiDelta;
        if (this.codexTimer <= 0) {
          this.codexTimer = CODEX_DECIDE_INTERVAL;
          const commands = this.enemyAI.decide(this.buildBattleSnapshot());
          this.applyCodexCommands(commands);
        }
      }
    }

    this.updateMusicPress(delta);
    this.updateBaddieBeaconObjectives(delta);
    this.updateRivalVenueObjectives(delta);
    this.updateCorporateHQ(delta);
    this.updateBeaconPulseSystems(delta);
    this.updateFutureSongPrewarm();
    this.updateAnalyticsSnapshots(delta);
    this.mapRenderer.update(delta);
    if (this.autoplayActive) this.updateAutoplay(delta);

    // Enemy class introductions — one short reveal the first time a type enters play.
    while (this.waves.pendingEnemyIntroductions.length > 0) {
      const intro = this.waves.pendingEnemyIntroductions.shift()!;
      this.toasts.show(
        `NEW THREAT: ${intro.title} — ${intro.disruption}`,
        intro.severity,
        5.5,
      );
    }
    while (this.waves.pendingEnemyActivity.length > 0) {
      const activity = this.waves.pendingEnemyActivity.shift()!;
      this.toasts.show(`⚡ ${activity.message}`, activity.severity, 3.8);
    }

    // Fog of war — active good forces and opposition pieces both disclose the
    // map as they move. Enemy reveal makes attacks readable through fog.
    const revealingUnits = this.units.filter(u =>
      u.alive &&
      !u.isDowned &&
      u.specialRole !== 'corporateHQ' &&
      (this.corporateHQDiscovered || !this.isInsideHiddenCorporateEstate(u.gridPos)) &&
      (
        u.faction === Faction.Player ||
        u.faction === Faction.Enemy ||
        u.enemyType !== null
      ),
    );
    this.fog.update(revealingUnits, delta);

    // Award resources for enemy kills, then prune dead units
    let pruned = false;
    for (let i = this.units.length - 1; i >= 0; i--) {
      const u = this.units[i];
      if (!u.alive) {
        if (u.faction === Faction.Enemy) {
          this.resources.onEnemyKilled(u.enemyType, u.displayName);
          this.enemiesDefeated++;
          trackBattleEvent('enemy_killed', {
            elapsed: this.elapsed,
            wave: this.waves.currentWaveIndex,
            enemyType: u.enemyType,
            displayName: u.displayName,
            specialRole: u.specialRole,
          });
        }
        if (u.specialRole === 'corporateHQ') {
          this.corporateHQDestroyed = true;
          this.toasts.show('✓ Corporate HQ destroyed — fake-pop production halted!', 'success', 6.0);
          trackBattleEvent('corporate_hq_destroyed', {
            elapsed: this.elapsed,
            wave: this.waves.currentWaveIndex,
          });
        }
        if (u.specialRole === 'rivalVenue') {
          this.toasts.show(
            u.constructionComplete
              ? `✓ Level ${u.rivalVenueLevel} Rival Venue shut down — local Industry spawns halted!`
              : '✓ Rival Venue build stopped before opening night!',
            'success',
            4.5,
          );
          trackBattleEvent('rival_venue_destroyed', {
            elapsed: this.elapsed,
            wave: this.waves.currentWaveIndex,
            completed: u.constructionComplete,
            level: u.rivalVenueLevel,
            age: Math.round(u.rivalVenueAge),
          });
        }
        this.unitRenderer.remove(u);
        this.units.splice(i, 1);
        pruned = true;
      }
    }
    if (pruned) this.selection.setUnits(this.units);

    // Streaming Moguls exert passive global pressure while alive. Cap the
    // stack so a late wave cannot erase the economy in a single tick.
    const activeMogulCount = Math.min(
      MOGUL_GLOBAL_DRAIN_CAP,
      this.units.filter(unit =>
        unit.alive &&
        !unit.isDowned &&
        unit.faction === Faction.Enemy &&
        unit.enemyType === 'StreamingMogul',
      ).length,
    );
    this.resources.setEnemySalesDrain(activeMogulCount * MOGUL_SALES_DRAIN);

    // Resource income tick
    this.resources.update(delta);
    this.updateProgressionToasts();

    // Beacon signal — uses real-time delta (not tempo-scaled)
    // Count living units within BEACON_NEAR_RADIUS of the beacon centre (19.5, 14.5)
    const bcCol = this.map.beacon.col + 0.5;
    const bcRow = this.map.beacon.row + 0.5;
    let nearFriendly = 0;
    let nearEnemies  = 0;
    for (const u of this.units) {
      if (!u.alive) continue;
      if (u.isDowned) continue;
      const d = Math.hypot(u.gridPos.col - bcCol, u.gridPos.row - bcRow);
      if (d > BEACON_NEAR_RADIUS) continue;
      if (u.faction === Faction.Player) nearFriendly++;
      else if (u.faction === Faction.Enemy) nearEnemies++;
    }
    const rivalBeaconJamming = this.content.balance.baddieBeaconJamming * this.baddieBeaconJammingMultiplier();
    const mogulJamming = activeMogulCount * MOGUL_SIGNAL_DRAIN;
    // Nigel (songwriter) near the beacon amplifies signal generation
    const nigel = this.units.find(
      u => u.alive && !u.isDowned && u.faction === Faction.Player && u.bandClass === 'songwriter',
    );
    const nigelDist = nigel
      ? Math.hypot(nigel.gridPos.col - bcCol, nigel.gridPos.row - bcRow)
      : Infinity;
    const nigelNear = nigelDist <= NIGEL_PROXIMITY_RADIUS;
    const effectiveNearFriendly = nearFriendly + (nigelNear ? NIGEL_SIGNAL_BONUS : 0);
    this.beaconSystem.update(delta, effectiveNearFriendly, nearEnemies, rivalBeaconJamming + mogulJamming);

    // Gear depot mining — fans near depots extract GEAR into the resource pool
    this.gearSystem.update(this.units, delta);

    // Stage 8: player-placed buildings — construction + income + Sound Rig attacks
    this.placement.update(delta, this.units);

    // Venue claiming / defending
    this.build.update(this.units, delta);

    // Spawn fan units queued by BuildSystem when venues are claimed
    while (this.build.pendingFanSpawns.length > 0) {
      const pos = this.build.pendingFanSpawns.shift()!;
      this.toasts.show('◈ Venue booked — fans deployed!', 'success', 3.0);
      for (let i = 0; i < FAN_SPAWN_COUNT; i++) {
        this.spawnFan(pos, i);
      }
    }

    // Toast when a fan workshop finishes construction
    while (this.build.pendingBuildCompletions.length > 0) {
      const name = this.build.pendingBuildCompletions.shift()!;
      this.toasts.show(`⚒ ${name} constructed — ready to claim!`, 'success', 5.0);
    }

    // Affordability hints — fired once when a unit arrives at a venue the
    // player can't yet afford, so it's clear it's a resource issue not a bug.
    while (this.build.pendingAffordabilityHints.length > 0) {
      const h = this.build.pendingAffordabilityHints.shift()!;
      this.toasts.show(
        `♪ ${h.name} needs ${h.repCost} REP + ${h.salesCost} SALES to book${h.reason ? ` — ${h.reason}` : ''}`,
        'warning',
        4.0,
      );
    }

    // Venue under attack — edge-triggered warning
    while (this.build.pendingVenueAttacks.length > 0) {
      const name = this.build.pendingVenueAttacks.shift()!;
      this.toasts.show(`⚠ ${name} is under attack!`, 'warning', 3.5);
    }

    // Venue destroyed — danger alert
    while (this.build.pendingVenueDestroyed.length > 0) {
      const name = this.build.pendingVenueDestroyed.shift()!;
      this.toasts.show(`✘ ${name} has been shut down`, 'danger', 4.0);
    }

    // Engagement warning — crowd drifting, player needs to revisit venue
    while (this.build.pendingEngagementWarnings.length > 0) {
      const name = this.build.pendingEngagementWarnings.shift()!;
      this.toasts.show(`♪ ${name} — crowd drifting. Time to play a set.`, 'warning', 4.5);
    }

    // Nigel idle-gravity: when Nigel has no orders and isn't near the beacon,
    // automatically walk him back so his proximity bonus stays active.
    this.nigelIdleTimer -= delta;
    if (this.nigelIdleTimer <= 0) {
      this.nigelIdleTimer = 10;
      const nigelUnit = this.units.find(
        u => u.alive && !u.isDowned && u.faction === Faction.Player && u.bandClass === 'songwriter',
      );
      if (nigelUnit && nigelUnit.path.length === 0 && nigelUnit.combatOrder === 'idle') {
        const beaconDest = { col: this.map.beacon.col, row: this.map.beacon.row };
        const dist = Math.hypot(nigelUnit.gridPos.col - beaconDest.col, nigelUnit.gridPos.row - beaconDest.row);
        if (dist > NIGEL_PROXIMITY_RADIUS * 0.6) {
          nigelUnit.issueMove(this.pathfinding.findPath(nigelUnit.gridPos, beaconDest), beaconDest);
        }
      }
    }

    // ── Toast triggers — fire once per state change ───────────────────────────
    // Wave advance
    const waveNow = this.waves.currentWaveIndex;
    if (waveNow > this.prevWave) {
      if (waveNow > 0) this.toasts.show(`⚡ WAVE ${waveNow} INCOMING`, 'warning', 4.5);
      trackBattleEvent('wave_started', { elapsed: this.elapsed, wave: waveNow });
      this.prevWave = waveNow;
    }
    // Beacon signal band change
    const bandNow = this.beaconSystem.signalBand;
    if (bandNow !== this.prevBand) {
      if (bandNow === 'critical')  this.toasts.show('⚠ SIGNAL CRITICAL — defend the Beacon!', 'danger',  5.5);
      if (bandNow === 'reduced')   this.toasts.show('⚠ Signal weakening — enemies nearby?',   'warning', 3.5);
      if (bandNow === 'prophecy')  this.toasts.show('◈ Prophecy active — signal strong!',     'success', 3.0);
      trackBattleEvent('signal_band_changed', {
        elapsed: this.elapsed,
        wave: this.waves.currentWaveIndex,
        band: bandNow,
        signal: Math.round(this.beaconSystem.signal),
        beaconOwner: this.beaconSystem.beaconOwner,
      });
      this.prevBand = bandNow;
    }
    // Song drop — auto-fires once the signal sustain is achieved
    const songNow = this.beaconSystem.songDropReady;
    if (songNow && !this.prevSongDrop) {
      this.activateSongDrop();
    }
    this.prevSongDrop = this.beaconSystem.songDropReady; // re-read after possible consume

    // Central Future Beacon ownership — capture is pressure, not instant loss.
    const beaconOwnerNow = this.beaconSystem.beaconOwner;
    if (beaconOwnerNow !== this.prevBeaconOwner) {
      if (beaconOwnerNow === 'enemy') {
        this.toasts.show('⚠ FUTURE BEACON TAKEN — red signal pulses are draining the band!', 'danger', 6.0);
        this.mapRenderer.triggerPulse(0xff2244, 3.0);
        trackBattleEvent('beacon_captured', { elapsed: this.elapsed, wave: this.waves.currentWaveIndex });
      } else {
        this.toasts.show('◈ Future Beacon reclaimed — signal restored!', 'success', 5.0);
        this.mapRenderer.triggerPulse(0x37e0d0, 2.5);
        trackBattleEvent('beacon_reclaimed', { elapsed: this.elapsed, wave: this.waves.currentWaveIndex });
      }
      this.prevBeaconOwner = beaconOwnerNow;
    }

    // Defeat detection — fans gone and all band members neutralised.
    if (this.areGoodForcesNeutralised() && !this.gameOverScreen.isVisible) {
      this.triggerGameOver();
    }

    // Move indicators
    this.updateIndicators(delta);
    this.updateTorrentShots(delta);
  }

  private startCinematicZoom(): void {
    this.camera.setZoomCentered(2.0);
    this.cinemaZoom = { elapsed: 0, duration: 3.0, startZoom: 2.0, targetZoom: 0.5 };
  }

  private updateCinematicZoom(delta: number): void {
    if (!this.cinemaZoom) return;
    this.cinemaZoom.elapsed += delta;
    const t = Math.min(this.cinemaZoom.elapsed / this.cinemaZoom.duration, 1);
    // Ease-out cubic: fast start, gentle finish
    const eased = 1 - Math.pow(1 - t, 3);
    const z = this.cinemaZoom.startZoom + (this.cinemaZoom.targetZoom - this.cinemaZoom.startZoom) * eased;
    this.camera.setZoomCentered(z);
    if (t >= 1) this.cinemaZoom = null;
  }

  private render(): void {
    this.camera.apply();
    this.mapRenderer.render(this.elapsed);

    // Rebuild the fog overlay whenever fog state changes OR the camera moves.
    // Camera movement shifts the viewport so previously-culled tiles become
    // visible — without a rebuild those tiles would show unfogged terrain.
    const tl = this.camera.topLeft;
    const cz = this.camera.currentZoom;
    const camerasMoved = tl.x !== this.lastCamX || tl.y !== this.lastCamY || cz !== this.lastCamZ;
    if (this.fog.dirty || camerasMoved) {
      this.fogRenderer.rebuild(this.fog.fog);
      this.fog.dirty  = false;
      this.lastCamX = tl.x;
      this.lastCamY = tl.y;
      this.lastCamZ = cz;
    }

    this.unitRenderer.render(this.units, this.elapsed);
    this.venueRenderer.render(this.build.venues, this.elapsed);
    this.venueRenderer.renderBuildSites(this.build.buildSites, this.elapsed);
    this.gearNodeRenderer.render(this.gearSystem.nodes, this.elapsed);

    // Stage 8: render player-placed buildings and placement ghost
    this.buildingRenderer.render(this.placement.buildings, this.elapsed);
    const gt = this.placement.ghostTile;
    const pt = this.placement.placementType;
    if (pt && gt) {
      const venueOccupied = (c: number, r: number) =>
        this.build.venues.some(v => v.buildingCol === c && v.buildingRow === r);
      const valid = this.placement.canPlace(gt.col, gt.row, venueOccupied);
      this.buildingRenderer.renderGhost(gt.col, gt.row, valid);
    } else {
      this.buildingRenderer.renderGhost(null, null, false);
    }

    this.hud.update();
    this.hud.setElapsed(this.elapsed);
    this.hud.setWave(this.waves.currentWaveIndex);
    this.updateOverlay();
  }

  private updateMusicPress(delta: number): void {
    for (const unit of this.units) {
      if (unit.enemyType !== 'MusicPress' || unit.faction !== Faction.Neutral) continue;
      if (unit.pressHostileTimer == null) continue;

      unit.pressHostileTimer = Math.max(0, unit.pressHostileTimer - delta);
      if (unit.pressHostileTimer === 0) {
        unit.faction = Faction.Enemy;
        unit.displayName = 'Hostile Music Press';
        this.resources.startBadReview(MUSIC_PRESS_BAD_REVIEW_DURATION);
        this.toasts.show(
          `⚠ BAD REVIEW — Pub reputation income reduced for ${MUSIC_PRESS_BAD_REVIEW_DURATION}s`,
          'danger',
          5.5,
        );
        trackBattleEvent('music_press_hostile', {
          elapsed: this.elapsed,
          wave: this.waves.currentWaveIndex,
          penaltySeconds: MUSIC_PRESS_BAD_REVIEW_DURATION,
        });
      }
    }
  }

  private onMusicPressConverted(press: Unit): void {
    this.resources.add('reputation', MUSIC_PRESS_REP_REWARD);
    this.resources.add('fanbase', MUSIC_PRESS_FAN_REWARD);
    this.toasts.show(
      `◈ PRESS WON OVER — +${MUSIC_PRESS_REP_REWARD} REP · +${MUSIC_PRESS_FAN_REWARD} FAN`,
      'success',
      5.0,
    );
    this.mapRenderer.triggerPulse(0x37e0d0, 1.8);
    trackBattleEvent('music_press_converted', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      unitId: press.id,
      reputation: MUSIC_PRESS_REP_REWARD,
      fanbase: MUSIC_PRESS_FAN_REWARD,
    });
  }

  private onBadManagerDetected(manager: Unit): void {
    this.toasts.show('◈ Keyboard Prophets exposed a Bad Manager — infiltration blocked!', 'success', 4.0);
    trackBattleEvent('bad_manager_detected', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      unitId: manager.id,
      detectionRadius: BADMANAGER_DETECTION_RADIUS,
    });
  }

  private isInsideHiddenCorporateEstate(pos: GridPosition): boolean {
    const hq = this.map.corporateHQ;
    return Math.hypot(pos.col - hq.buildingCol, pos.row - hq.buildingRow) <= CORPORATE_HQ_DISCOVERY_RADIUS + 3;
  }

  private updateCorporateHQ(delta: number): void {
    const hq = this.units.find(u => u.alive && u.specialRole === 'corporateHQ');
    if (!hq) return;

    if (!this.corporateHQDiscovered) {
      const access = this.map.corporateHQ;
      const discovered = this.units.some(unit =>
        unit.alive &&
        !unit.isDowned &&
        unit.faction === Faction.Player &&
        Math.hypot(unit.gridPos.col - access.accessCol, unit.gridPos.row - access.accessRow) <= CORPORATE_HQ_DISCOVERY_RADIUS,
      );
      if (discovered) {
        this.corporateHQDiscovered = true;
        this.toasts.show('⚠ Corporate HQ discovered — fake-pop factory is manufacturing pressure!', 'danger', 6.0);
        this.mapRenderer.triggerPulse(0xff2244, 2.5);
        trackBattleEvent('corporate_hq_discovered', {
          elapsed: this.elapsed,
          wave: this.waves.currentWaveIndex,
          col: access.buildingCol,
          row: access.buildingRow,
        });
      }
    }

    // The secret factory must not announce itself during the first meaningful
    // fights. Until discovered, it stays silent until late game; after that it
    // can seed wider fog pressure without exposing the estate.
    const wave = this.waves.currentWaveIndex;
    if (wave < 1) return;
    this.updateCorporateHQHintPulse(delta, hq);
    if (!this.corporateHQDiscovered && wave < CORPORATE_HQ_HIDDEN_ACTIVATION_WAVE) return;

    this.updateCorporateHQSecurity(delta);
    this.corporateHQSpawnTimer += delta * (this.corporateHQDiscovered ? 1.35 : 1);
    if (this.corporateHQSpawnTimer < CORPORATE_HQ_SPAWN_INTERVAL) return;
    this.corporateHQSpawnTimer = 0;

    const roster: EnemyType[] = wave >= 4
      ? ['GenZSwarm', 'BadManager', 'FilePirate']
      : wave >= 2
        ? ['GenZSwarm', 'FilePirate']
        : ['GenZSwarm'];
    const count = Math.min(1 + Math.floor(wave / 3), 3);
    let spawned = 0;
    let localSpawned = 0;
    let fogSpawned = 0;
    const localQuota = this.corporateHQDiscovered && count >= 3 ? 1 : 0;
    for (let i = 0; i < count; i++) {
      const type = roster[(wave + i) % roster.length];
      const unit = i < localQuota
        ? this.waves.spawnFromCorporateHQ(type)
        : this.waves.spawnFromFog(type);
      if (!unit) continue;
      spawned++;
      if (i < localQuota) localSpawned++;
      else fogSpawned++;
    }
    if (spawned > 0 && this.corporateHQDiscovered) {
      const source = fogSpawned > localSpawned ? 'seeded threats through the wider fog' : 'manufactured new local threats';
      this.toasts.show(`⚠ Corporate HQ ${source}!`, 'warning', 4.0);
    }
    if (spawned > 0) {
      trackBattleEvent('corporate_hq_spawned', {
        elapsed: this.elapsed,
        wave: this.waves.currentWaveIndex,
        count: spawned,
        localSpawned,
        fogSpawned,
      });
    }
  }

  private updateCorporateHQHintPulse(delta: number, hq: Unit): void {
    const wave = this.waves.currentWaveIndex;
    if (this.corporateHQDestroyed || wave < CORPORATE_HQ_HINT_PULSE_WAVE) return;

    this.corporateHQHintPulseTimer += delta;
    const interval = this.corporateHQDiscovered
      ? CORPORATE_HQ_HINT_PULSE_INTERVAL * 0.65
      : CORPORATE_HQ_HINT_PULSE_INTERVAL;
    if (this.corporateHQHintPulseTimer < interval) return;
    this.corporateHQHintPulseTimer = 0;

    const colour = this.corporateHQDiscovered ? 0xff2244 : 0x8b2cff;
    const alpha = this.corporateHQDiscovered ? 0.46 : 0.28;
    const radius = this.corporateHQDiscovered ? 980 : 620;
    this.mapRenderer.triggerPulseAt(hq.gridPos, colour, 2.8, radius, alpha);

    const visible = this.fog.getState(hq.gridPos.col, hq.gridPos.row) === FogState.Visible;
    if (!this.corporateHQDiscovered && !visible) {
      this.toasts.show('◈ A distant corporate signal is leaking through the fog…', 'warning', 4.2);
    }

    trackBattleEvent('corporate_hq_signal_pulse', {
      elapsed: this.elapsed,
      wave,
      discovered: this.corporateHQDiscovered,
      visible,
      col: hq.gridPos.col,
      row: hq.gridPos.row,
    });
  }

  private updateCorporateHQSecurity(delta: number): void {
    if (!this.corporateHQDiscovered) return;

    const access = this.map.corporateHQ;
    const campers = this.units.filter(unit =>
      unit.alive &&
      !unit.isDowned &&
      unit.faction === Faction.Player &&
      Math.hypot(unit.gridPos.col - access.accessCol, unit.gridPos.row - access.accessRow) <= CORPORATE_HQ_CAMP_RADIUS,
    );

    if (campers.length < 3) {
      this.corporateHQSecurityTimer = Math.max(0, this.corporateHQSecurityTimer - delta * 0.5);
      return;
    }

    this.corporateHQSecurityTimer += delta * Math.min(1.75, 1 + (campers.length - 3) * 0.18);
    if (this.corporateHQSecurityTimer < CORPORATE_HQ_SECURITY_INTERVAL) return;
    this.corporateHQSecurityTimer = 0;

    const roster: EnemyType[] = campers.length >= 5
      ? ['FilePirate', 'BadManager', 'GenZSwarm']
      : ['FilePirate', 'GenZSwarm'];
    const count = Math.min(roster.length, 1 + Math.floor(campers.length / 3));
    const sortedCampers = [...campers].sort((a, b) => {
      const da = Math.hypot(a.gridPos.col - access.accessCol, a.gridPos.row - access.accessRow);
      const db = Math.hypot(b.gridPos.col - access.accessCol, b.gridPos.row - access.accessRow);
      return da - db;
    });

    let spawned = 0;
    for (let i = 0; i < count; i++) {
      const guard = this.waves.spawnFromFog(roster[i]);
      const target = sortedCampers[i % sortedCampers.length];
      if (!guard || !target) continue;
      guard.issueAttackTarget(target);
      spawned++;
    }

    if (spawned > 0) {
      this.toasts.show(`⚠ Corporate HQ rerouted ${spawned} anti-camp unit${spawned === 1 ? '' : 's'} through the fog!`, 'danger', 4.5);
      this.mapRenderer.triggerPulse(0xff3344, 1.6);
      trackBattleEvent('corporate_hq_security_dispatch', {
        elapsed: this.elapsed,
        wave: this.waves.currentWaveIndex,
        campers: campers.length,
        spawned,
      });
    }
  }

  private spawnCorporateHQ(): Unit {
    const hqLayout = this.map.corporateHQ;
    const hq = new Unit('corporate_hq');
    hq.gridPos = { col: hqLayout.buildingCol, row: hqLayout.buildingRow };
    hq.faction = Faction.Enemy;
    hq.enemyType = 'BadManager';
    hq.displayName = 'Corporate HQ';
    hq.specialRole = 'corporateHQ';
    hq.threatScale = 2.2;
    hq.maxHp = CORPORATE_HQ_HP;
    hq.hp = hq.maxHp;
    hq.moveSpeed = 0;
    hq.attackDamage = 0;
    hq.attackRate = 1;
    hq.sightRadius = 0;
    hq.attackRadius = 0;
    this.units.push(hq);
    this.unitRenderer.add(hq);
    return hq;
  }

  private ensureCorporateHQExists(): void {
    if (this.corporateHQDestroyed) return;
    const existing = this.units.some(u => u.alive && u.specialRole === 'corporateHQ');
    if (!existing) this.spawnCorporateHQ();
  }

  private updateBaddieBeaconObjectives(delta: number): void {
    this.resolveDestroyedBaddieBeacons();

    const monoliths = this.units.filter(
      u => u.alive && u.specialRole === 'streamingMonolith' && u.objectiveTarget,
    );

    for (const monolith of monoliths) {
      const target = monolith.objectiveTarget!;
      const dist = Math.hypot(monolith.gridPos.col - target.col, monolith.gridPos.row - target.row);
      if (dist > 1.5) {
        monolith.isConstructing = false;
        if (!monolith.isMoving && !monolith.target) {
          monolith.setPath(this.pathfinding.findPath(monolith.gridPos, target));
        }
        continue;
      }

      if (monolith.gridPos.col === target.col && monolith.gridPos.row === target.row) {
        const buildSpot = this.nearestFreeTile(target, monolith);
        if (buildSpot) {
          monolith.isConstructing = false;
          if (!monolith.isMoving) {
            monolith.setPath(this.pathfinding.findPath(monolith.gridPos, buildSpot));
          }
          continue;
        }
      }

      monolith.path = [];
      monolith.moveT = 0;
      monolith.isConstructing = true;
      let beacon = this.findLinkedBaddieBeacon(monolith);
      if (!beacon) {
        beacon = this.spawnBaddieBeacon(monolith, target);
      }
      if (!beacon.alive || beacon.constructionComplete) continue;

      beacon.constructionProgress = Math.min(
        1,
        beacon.constructionProgress + delta / this.content.balance.baddieBeaconBuildTime,
      );
      monolith.attackPulse = Math.max(monolith.attackPulse, 0.08);

      if (beacon.constructionProgress >= 1) {
        beacon.constructionComplete = true;
        beacon.displayName = 'Baddie Beacon';
        monolith.isConstructing = false;
        monolith.objectiveTarget = null;
        monolith.clearCombatOrder();
        this.spawnBaddieBeaconGuards(beacon, BADDIE_BEACON_ONLINE_GUARDS);
        this.spawnBaddieBeaconRepairCrews(beacon, BADDIE_BEACON_REPAIR_CREWS);
        this.toasts.show('⚠ Baddie Beacon online — signal is being jammed!', 'danger', 6.0);
      }
    }

    for (const beacon of this.units) {
      if (!beacon.alive || beacon.specialRole !== 'baddieBeacon' || beacon.constructionComplete) continue;
      const builderAlive = this.units.some(
        u => u.alive && u.specialRole === 'streamingMonolith' && u.id === beacon.linkedUnitId,
      );
      if (!builderAlive) {
        beacon.alive = false;
        this.toasts.show('✓ Rival Beacon construction collapsed!', 'success', 4.0);
      }
    }
  }

  private resolveDestroyedBaddieBeacons(): void {
    for (const beacon of this.units) {
      if (beacon.alive || beacon.specialRole !== 'baddieBeacon') continue;
      const builder = this.units.find(u => u.id === beacon.linkedUnitId);
      if (builder?.specialRole === 'streamingMonolith') {
        builder.objectiveTarget = null;
        builder.isConstructing = false;
        builder.target = null;
        builder.path = [];
        builder.moveT = 0;
      }
      if (!beacon.constructionComplete) {
        this.toasts.show('✓ Baddie Beacon smashed before broadcast!', 'success', 4.5);
      } else {
        this.toasts.show('✓ Baddie Beacon destroyed — signal clear!', 'success', 4.5);
      }
    }
  }

  private findLinkedBaddieBeacon(monolith: Unit): Unit | null {
    return this.units.find(
      u => u.alive && u.specialRole === 'baddieBeacon' && u.linkedUnitId === monolith.id,
    ) ?? null;
  }

  private spawnBaddieBeacon(monolith: Unit, pos: GridPosition): Unit {
    const beacon = new Unit();
    beacon.gridPos = { ...pos };
    beacon.faction = Faction.Enemy;
    beacon.enemyType = 'StreamingMogul';
    beacon.displayName = 'Baddie Beacon (Building)';
    beacon.specialRole = 'baddieBeacon';
    beacon.linkedUnitId = monolith.id;
    beacon.threatScale = 1.45;
    beacon.maxHp = this.content.balance.baddieBeaconHp;
    beacon.hp    = this.content.balance.baddieBeaconHp;
    beacon.moveSpeed = 0;
    beacon.attackDamage = 0;
    beacon.attackRate = 1;
    beacon.sightRadius = 0;
    beacon.attackRadius = 0;
    beacon.constructionProgress = 0;
    beacon.constructionComplete = false;
    this.units.push(beacon);
    this.unitRenderer.add(beacon);
    this.toasts.show('⚠ Streaming Monolith is building a Baddie Beacon!', 'danger', 6.0);
    return beacon;
  }

  private updateRivalVenueObjectives(delta: number): void {
    this.assignRivalVenueBuilderTargets();
    this.resolveOrphanedRivalVenueBuilds();

    const builders = this.units.filter(
      u => u.alive && u.specialRole === 'rivalVenueBuilder',
    );

    for (const builder of builders) {
      const target = builder.objectiveTarget;
      if (!target) continue;

      const dist = Math.hypot(builder.gridPos.col - target.col, builder.gridPos.row - target.row);
      if (dist > 1.2) {
        builder.isConstructing = false;
        if (!builder.isMoving && !builder.target) {
          builder.setPath(this.pathfinding.findPath(builder.gridPos, target));
        }
        continue;
      }

      builder.path = [];
      builder.moveT = 0;
      builder.target = null;
      builder.isConstructing = true;
      let venue = this.findLinkedRivalVenue(builder);
      if (!venue) venue = this.spawnRivalVenueConstruction(builder, target);
      if (!venue.alive || venue.constructionComplete) continue;

      venue.constructionProgress = Math.min(1, venue.constructionProgress + delta / RIVAL_VENUE_BUILD_TIME);
      builder.attackPulse = Math.max(builder.attackPulse, 0.08);

      if (venue.constructionProgress >= 1) {
        venue.constructionComplete = true;
        venue.rivalVenueLevel = 1;
        venue.rivalVenueAge = 0;
        venue.rivalVenueNextEscalation = RIVAL_VENUE_LEVEL_2_TIME;
        venue.displayName = this.rivalVenueDisplayName(venue);
        venue.spawnTimer = RIVAL_VENUE_SPAWN_INTERVAL * 0.45;
        builder.isConstructing = false;
        builder.objectiveTarget = null;
        builder.enemyType = null;
        builder.faction = Faction.Neutral;
        builder.alive = false;
        this.toasts.show('⚠ Rival Venue open — shut it down before it grows a crowd!', 'danger', 5.5);
        trackBattleEvent('rival_venue_completed', {
          elapsed: this.elapsed,
          wave: this.waves.currentWaveIndex,
          col: venue.gridPos.col,
          row: venue.gridPos.row,
          level: venue.rivalVenueLevel,
        });
      }
    }

    for (const venue of this.units.filter(u => u.alive && u.specialRole === 'rivalVenue' && u.constructionComplete)) {
      this.updateRivalVenueEscalation(venue, delta);
      venue.spawnTimer += delta;
      const interval = this.rivalVenueSpawnInterval(venue);
      if (venue.spawnTimer < interval) continue;
      venue.spawnTimer = 0;
      this.spawnFromRivalVenue(venue);
    }
  }

  private updateRivalVenueEscalation(venue: Unit, delta: number): void {
    venue.rivalVenueAge += delta;
    if (venue.rivalVenueNextEscalation <= 0) {
      venue.rivalVenueNextEscalation = nextRivalVenueEscalationTime(venue.rivalVenueLevel);
    }
    if (venue.rivalVenueNextEscalation === Infinity || venue.rivalVenueAge < venue.rivalVenueNextEscalation) return;

    const previousLevel = venue.rivalVenueLevel;
    venue.rivalVenueLevel = Math.min(3, venue.rivalVenueLevel + 1);
    venue.rivalVenueNextEscalation = nextRivalVenueEscalationTime(venue.rivalVenueLevel);
    const hpRatio = venue.maxHp > 0 ? venue.hp / venue.maxHp : 1;
    venue.maxHp = RIVAL_VENUE_HP + (venue.rivalVenueLevel - 1) * RIVAL_VENUE_LEVEL_HP_BONUS;
    venue.hp = Math.max(1, Math.round(venue.maxHp * Math.max(hpRatio, 0.72)));
    venue.threatScale = 1.45 + venue.rivalVenueLevel * 0.16;
    venue.displayName = this.rivalVenueDisplayName(venue);
    venue.attackPulse = Math.max(venue.attackPulse, 0.45);

    this.toasts.show(
      `⚠ ${venue.displayName} has grown a bigger crowd — shut it down!`,
      venue.rivalVenueLevel >= 3 ? 'danger' : 'warning',
      5.0,
    );
    trackBattleEvent('rival_venue_escalated', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      previousLevel,
      level: venue.rivalVenueLevel,
      age: Math.round(venue.rivalVenueAge),
      col: venue.gridPos.col,
      row: venue.gridPos.row,
    });
  }

  private assignRivalVenueBuilderTargets(): void {
    for (const builder of this.units) {
      if (!builder.alive || builder.specialRole !== 'rivalVenueBuilder' || builder.objectiveTarget) continue;
      if (this.countActiveRivalVenues() >= RIVAL_VENUE_MAX_ACTIVE) {
        builder.isRetreating = true;
        continue;
      }
      const target = this.pickRivalVenueSite(builder);
      if (!target) {
        builder.isRetreating = true;
        continue;
      }
      builder.objectiveTarget = target;
      builder.commandTarget = target;
      this.toasts.show('⚠ Rival Venue Builder spotted — intercept it before it opens a venue!', 'warning', 5.0);
    }
  }

  private resolveOrphanedRivalVenueBuilds(): void {
    for (const venue of this.units) {
      if (!venue.alive || venue.specialRole !== 'rivalVenue' || venue.constructionComplete) continue;
      const builderAlive = this.units.some(
        u => u.alive && u.specialRole === 'rivalVenueBuilder' && u.id === venue.linkedUnitId,
      );
      if (!builderAlive) venue.alive = false;
    }
  }

  private countActiveRivalVenues(): number {
    return this.units.filter(
      u => u.alive && u.specialRole === 'rivalVenue',
    ).length;
  }

  private findLinkedRivalVenue(builder: Unit): Unit | null {
    return this.units.find(
      u => u.alive && u.specialRole === 'rivalVenue' && u.linkedUnitId === builder.id,
    ) ?? null;
  }

  private spawnRivalVenueConstruction(builder: Unit, pos: GridPosition): Unit {
    const venue = new Unit();
    venue.gridPos = { ...pos };
    venue.faction = Faction.Enemy;
    venue.enemyType = 'RivalVenueBuilder';
    venue.displayName = 'Rival Venue (Building)';
    venue.specialRole = 'rivalVenue';
    venue.linkedUnitId = builder.id;
    venue.threatScale = 1.55;
    venue.maxHp = RIVAL_VENUE_HP;
    venue.hp = RIVAL_VENUE_HP;
    venue.moveSpeed = 0;
    venue.attackDamage = 0;
    venue.attackRate = 1;
    venue.sightRadius = 0;
    venue.attackRadius = 0;
    venue.constructionProgress = 0;
    venue.constructionComplete = false;
    venue.spawnTimer = 0;
    venue.rivalVenueLevel = 1;
    venue.rivalVenueAge = 0;
    venue.rivalVenueNextEscalation = RIVAL_VENUE_LEVEL_2_TIME;
    this.units.push(venue);
    this.unitRenderer.add(venue);
    this.toasts.show('⚠ Rival Venue construction started!', 'danger', 5.5);
    trackBattleEvent('rival_venue_started', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      col: pos.col,
      row: pos.row,
    });
    return venue;
  }

  private pickRivalVenueSite(builder: Unit): GridPosition | null {
    const beacon = { col: this.map.beacon.col, row: this.map.beacon.row };
    let best: { pos: GridPosition; score: number } | null = null;

    for (let radius = RIVAL_VENUE_SITE_RADIUS_MIN; radius <= RIVAL_VENUE_SITE_RADIUS_MAX; radius += 3) {
      for (let attempts = 0; attempts < 28; attempts++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = radius + Math.random() * 3;
        const pos = {
          col: Math.round(builder.gridPos.col + Math.cos(angle) * dist),
          row: Math.round(builder.gridPos.row + Math.sin(angle) * dist),
        };
        if (!this.isValidRivalVenueSite(pos, builder)) continue;

        const path = this.pathfinding.findPath(builder.gridPos, pos);
        if (path.length === 0) continue;

        const beaconDist = Math.hypot(pos.col - beacon.col, pos.row - beacon.row);
        const nearestClaimed = this.build.venues
          .filter(v => v.state === 'claimed')
          .reduce((min, v) => Math.min(min, Math.hypot(pos.col - v.col, pos.row - v.row)), 999);
        const score = nearestClaimed < 18 ? 40 - nearestClaimed : Math.max(0, 32 - beaconDist);
        if (!best || score > best.score) best = { pos, score };
      }
      if (best) return best.pos;
    }

    return null;
  }

  private isValidRivalVenueSite(pos: GridPosition, builder: Unit): boolean {
    if (!this.map.inBounds(pos.col, pos.row) || !this.map.isPassable(pos)) return false;
    if (this.isTileOccupied(pos, builder)) return false;
    if (this.isInsideHiddenCorporateEstate(pos)) return false;
    const beaconDist = Math.hypot(pos.col - this.map.beacon.col, pos.row - this.map.beacon.row);
    if (beaconDist < RIVAL_VENUE_MIN_BEACON_DISTANCE) return false;
    if (this.build.venues.some(v => Math.hypot(pos.col - v.col, pos.row - v.row) < 5)) return false;
    if (this.units.some(u => u.alive && u.specialRole === 'rivalVenue' && Math.hypot(pos.col - u.gridPos.col, pos.row - u.gridPos.row) < 14)) return false;
    return true;
  }

  private spawnFromRivalVenue(venue: Unit): void {
    const enemyTypes = this.rivalVenueSpawnMix(venue);
    let spawned = 0;
    for (const type of enemyTypes) {
      const pos = this.findSpawnTileNear(venue.gridPos, RIVAL_VENUE_SPAWN_RADIUS);
      if (!pos) continue;
      this.spawnLocalIndustryUnit(type, pos, this.rivalVenueSpawnName(type, venue.rivalVenueLevel));
      spawned++;
    }
    if (spawned > 0) {
      venue.attackPulse = Math.max(venue.attackPulse, 0.2);
      this.toasts.show(`⚠ ${venue.displayName} released ${spawned} Industry unit${spawned === 1 ? '' : 's'}!`, 'warning', 3.8);
      trackBattleEvent('rival_venue_spawned_units', {
        elapsed: this.elapsed,
        wave: this.waves.currentWaveIndex,
        count: spawned,
        level: venue.rivalVenueLevel,
        age: Math.round(venue.rivalVenueAge),
        spawnMix: enemyTypes,
        col: venue.gridPos.col,
        row: venue.gridPos.row,
      });
    }
  }

  private rivalVenueDisplayName(venue: Unit): string {
    if (venue.rivalVenueLevel >= 3) return 'Rival Venue L3';
    if (venue.rivalVenueLevel === 2) return 'Rival Venue L2';
    return 'Rival Venue';
  }

  private rivalVenueSpawnInterval(venue: Unit): number {
    const levelSteps = Math.max(0, venue.rivalVenueLevel - 1);
    return Math.max(15, RIVAL_VENUE_SPAWN_INTERVAL * Math.pow(RIVAL_VENUE_LEVEL_SPAWN_ACCEL, levelSteps));
  }

  private rivalVenueSpawnMix(venue: Unit): EnemyType[] {
    const lateGame = this.waves.currentWaveIndex >= 5;
    if (venue.rivalVenueLevel >= 3) {
      return lateGame
        ? ['GenZSwarm', 'GenZSwarm', 'BadManager', 'FilePirate', 'TributeAct']
        : ['GenZSwarm', 'GenZSwarm', 'BadManager', 'FilePirate'];
    }
    if (venue.rivalVenueLevel === 2) {
      return lateGame
        ? ['GenZSwarm', 'GenZSwarm', 'BadManager', 'FilePirate']
        : ['GenZSwarm', 'GenZSwarm', 'BadManager'];
    }
    return lateGame
      ? ['GenZSwarm', 'GenZSwarm', 'BadManager']
      : ['GenZSwarm', 'GenZSwarm'];
  }

  private rivalVenueSpawnName(type: EnemyType, level: number): string {
    if (type === 'BadManager') return level >= 3 ? 'Rival Venue Fixer' : 'Rival Venue Manager';
    if (type === 'FilePirate') return 'Rival Venue Pirate';
    if (type === 'TributeAct') return 'Rival Venue Headliner';
    return level >= 3 ? 'Rival Venue Street Team' : 'Rival Venue Runner';
  }

  private findSpawnTileNear(pos: GridPosition, radius: number): GridPosition | null {
    const offsets: GridPosition[] = [];
    for (let dc = -radius; dc <= radius; dc++) {
      for (let dr = -radius; dr <= radius; dr++) {
        if (dc === 0 && dr === 0) continue;
        offsets.push({ col: pos.col + dc, row: pos.row + dr });
      }
    }
    offsets.sort(() => Math.random() - 0.5);
    return offsets.find(candidate =>
      this.map.inBounds(candidate.col, candidate.row) &&
      this.map.isPassable(candidate) &&
      !this.isTileOccupied(candidate)
    ) ?? null;
  }

  private spawnLocalIndustryUnit(type: EnemyType, pos: GridPosition, displayName: string): Unit {
    const unit = new Unit();
    unit.gridPos = { ...pos };
    unit.faction = Faction.Enemy;
    unit.enemyType = type;
    unit.displayName = displayName;
    unit.threatScale = 0.95;
    switch (type) {
      case 'BadManager':
        unit.maxHp = this.content.balance.enemyHpBadManager;
        unit.attackDamage = this.content.balance.enemyDamageBadManager;
        unit.moveSpeed = this.content.balance.enemyMoveSpeedBadManager;
        break;
      case 'FilePirate':
        unit.maxHp = this.content.balance.enemyHpPirate;
        unit.attackDamage = this.content.balance.enemyDamagePirate;
        unit.moveSpeed = this.content.balance.enemyMoveSpeedPirate;
        unit.threatScale = 1.04;
        break;
      case 'TributeAct':
        unit.maxHp = this.content.balance.enemyHpTribute;
        unit.attackDamage = this.content.balance.enemyDamageTribute;
        unit.moveSpeed = this.content.balance.enemyMoveSpeedTribute;
        unit.threatScale = 1.18;
        break;
      case 'StreamingMogul':
        unit.maxHp = this.content.balance.enemyHpMogul;
        unit.attackDamage = 0;
        unit.moveSpeed = this.content.balance.enemyMoveSpeedMogul;
        unit.threatScale = 1.08;
        break;
      default:
        unit.maxHp = this.content.balance.enemyHpSwarm;
        unit.attackDamage = this.content.balance.enemyDamageSwarm;
        unit.moveSpeed = this.content.balance.enemyMoveSpeedSwarm;
        break;
    }
    unit.hp = unit.maxHp;
    unit.attackRate = this.content.balance.enemyAttackRate;
    unit.sightRadius = this.content.balance.enemySightRadius;
    unit.attackRadius = this.content.balance.enemyAttackRadius;
    if (type === 'FilePirate') {
      unit.attackRate *= 0.55;
      unit.sightRadius += 6;
      unit.attackRadius += 7;
    }
    if (type === 'GenZSwarm') unit.burstCycleTimer = GENZ_BURST_INTERVAL;
    if (type === 'StreamingMogul') unit.auraTimer = MOGUL_AURA_INTERVAL * 0.5;
    this.units.push(unit);
    this.unitRenderer.add(unit);
    return unit;
  }

  private updateBeaconPulseSystems(delta: number): void {
    if (this.baddieBeaconRepairToastCooldown > 0) {
      this.baddieBeaconRepairToastCooldown = Math.max(0, this.baddieBeaconRepairToastCooldown - delta);
    }

    const rivalBeaconActive = this.hasBaddieBeacon();
    if (rivalBeaconActive) {
      this.repairBaddieBeacons(delta);
      this.baddieBeaconPulseTimer += delta;
      if (this.baddieBeaconPulseTimer >= this.content.balance.baddieBeaconPulseTime) {
        this.baddieBeaconPulseTimer -= this.content.balance.baddieBeaconPulseTime;
        this.convertFansToBaddieWorkers();
      }
    } else {
      this.baddieBeaconPulseTimer = 0;
    }

    if (this.beaconSystem.isEnemyControlled) {
      this.goodBeaconPulseTimer = 0;
      this.capturedBeaconPulseTimer += delta;
      if (this.capturedBeaconPulseTimer >= CAPTURED_BEACON_PULSE_TIME) {
        this.capturedBeaconPulseTimer -= CAPTURED_BEACON_PULSE_TIME;
        this.damageGoodForcesFromCapturedBeacon();
      }
      return;
    }

    this.capturedBeaconPulseTimer = 0;
    if (rivalBeaconActive) {
      this.goodBeaconPulseTimer = 0;
      return;
    }

    this.goodBeaconPulseTimer += delta;
    if (this.goodBeaconPulseTimer >= this.nextGoodBeaconPulse) {
      this.goodBeaconPulseTimer = 0;
      this.nextGoodBeaconPulse = this.rollNextGoodBeaconPulse();
      this.healGoodForcesFromFutureSong();
    }
  }

  private hasBaddieBeacon(): boolean {
    return this.units.some(u => u.alive && u.specialRole === 'baddieBeacon');
  }

  private baddieBeaconJammingMultiplier(): number {
    const beacon = this.units.find(u => u.alive && u.specialRole === 'baddieBeacon');
    const beaconJamming = beacon
      ? beacon.constructionComplete ? 1 : Math.max(0.25, beacon.constructionProgress)
      : 0;
    return beaconJamming + this.activeSignalJammers() * SIGNAL_JAMMER_DRAIN;
  }

  private activeSignalJammers(): number {
    return this.units.filter(
      u => u.alive && !u.isDowned && u.faction === Faction.Enemy && u.displayName === 'Signal Jammer',
    ).length;
  }

  /** Composite score reflecting how much the player has built up. */
  private computeBuildScore(): number {
    const claimed     = this.build.venues.filter(v => v.state === 'claimed');
    const totalLevel  = claimed.reduce((sum, v) => sum + v.venueLevel, 0);
    const progress    = this.resources.progression;
    const tierBonus   = progress.tier === 'legend' ? 30 : progress.tier === 'breakout' ? 20 : progress.tier === 'buzz' ? 10 : 0;
    return claimed.length * 10 + totalLevel * 5 + Math.floor(progress.sceneScore / 80) + tierBonus;
  }

  private recordPlayerBattleLosses(): void {
    for (const unit of this.units) {
      if (unit.faction !== Faction.Player || !unit.bandClass || unit.bandClass === 'source') continue;
      const key = `${unit.id}:${unit.bandClass}`;
      if (this.reportedDownedUnits.has(key)) continue;

      if (unit.bandClass === 'fan' && !unit.alive) {
        this.reportedDownedUnits.add(key);
        trackBattleEvent('player_unit_lost', {
          elapsed: this.elapsed,
          wave: this.waves.currentWaveIndex,
          bandClass: unit.bandClass,
          col: unit.gridPos.col,
          row: unit.gridPos.row,
          resources: this.resources.snapshot,
        });
      } else if (unit.bandClass !== 'fan' && unit.isDowned) {
        this.reportedDownedUnits.add(key);
        trackBattleEvent('player_unit_downed', {
          elapsed: this.elapsed,
          wave: this.waves.currentWaveIndex,
          bandClass: unit.bandClass,
          displayName: unit.displayName,
          col: unit.gridPos.col,
          row: unit.gridPos.row,
          resources: this.resources.snapshot,
        });
      }
    }
  }

  private updateAnalyticsSnapshots(delta: number): void {
    if (this.beaconSystem.beaconOwner === 'enemy') this.enemyBeaconControlTime += delta;

    this.analyticsSnapshotTimer += delta;
    if (this.analyticsSnapshotTimer < 20) return;
    this.analyticsSnapshotTimer = 0;

    const resources = this.resources.snapshot;
    const playerUnits = this.units.filter(u => u.alive && u.faction === Faction.Player);
    const enemyUnits = this.units.filter(u => u.alive && u.faction === Faction.Enemy && u.specialRole !== 'rivalVenue');
    const enemyByType: Record<string, number> = {};
    for (const unit of enemyUnits) {
      const label = unit.displayName || unit.enemyType || unit.specialRole || 'Enemy';
      enemyByType[label] = (enemyByType[label] ?? 0) + 1;
    }
    const rivalVenues = this.units.filter(u => u.alive && u.specialRole === 'rivalVenue');
    const rivalVenueLevels = rivalVenues
      .filter(v => v.constructionComplete)
      .reduce<Record<string, number>>((acc, venue) => {
        const key = `level${venue.rivalVenueLevel}`;
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});
    const rivalVenueSpawnPressure = rivalVenues
      .filter(v => v.constructionComplete)
      .reduce((sum, venue) => sum + this.rivalVenueSpawnMix(venue).length / this.rivalVenueSpawnInterval(venue), 0);
    const downedBand = playerUnits.filter(u => u.isDowned && u.bandClass !== 'fan').length;
    const activeBand = playerUnits.filter(u => !u.isDowned && u.bandClass && u.bandClass !== 'fan' && u.bandClass !== 'source').length;
    const fansAlive = playerUnits.filter(u => !u.isDowned && u.bandClass === 'fan').length;
    const fogSeen = this.fog.fog.reduce((sum, state) => sum + (state === FogState.Unexplored ? 0 : 1), 0);
    const fogExploredPct = Math.round((fogSeen / Math.max(1, this.fog.fog.length)) * 100);
    const claimedVenues = this.build.venues.filter(v => v.state === 'claimed').length;
    const threatenedVenues = this.build.venues.filter(v => v.state === 'claimed' && v.isUnderAttack).length;

    trackBattleEvent('battle_snapshot', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      mapCode: this.map.mapCode,
      mapWidth: this.map.width,
      mapHeight: this.map.height,
      resources,
      sceneTier: this.resources.progression.tier,
      sceneScore: this.resources.progression.sceneScore,
      beaconOwner: this.beaconSystem.beaconOwner,
      beaconSignal: Math.round(this.beaconSystem.signal),
      beaconBand: this.beaconSystem.signalBand,
      beaconCaptureProgress: Number(this.beaconSystem.captureProgress.toFixed(2)),
      enemyBeaconControlTime: Math.round(this.enemyBeaconControlTime),
      activeBand,
      downedBand,
      fansAlive,
      playerUnits: playerUnits.length,
      enemyUnits: enemyUnits.length,
      enemyByType,
      claimedVenues,
      threatenedVenues,
      activeRivalVenues: rivalVenues.filter(v => v.constructionComplete).length,
      buildingRivalVenues: rivalVenues.filter(v => !v.constructionComplete).length,
      rivalVenueLevels,
      rivalVenueSpawnPressure: Number(rivalVenueSpawnPressure.toFixed(3)),
      maxRivalVenueLevel: rivalVenues.reduce((max, venue) => Math.max(max, venue.rivalVenueLevel), 0),
      corporateHQDiscovered: this.corporateHQDiscovered,
      corporateHQDestroyed: this.corporateHQDestroyed,
      fogExploredPct,
      autoplay: this.autoplayActive,
    });
  }

  private updateProgressionToasts(): void {
    const progress = this.resources.progression;
    if (progress.tier === this.prevSceneTier) return;

    if (progress.tier === 'buzz') {
      this.toasts.show('◈ Scene buzz building — Level 2 venue upgrades unlocked!', 'success', 5.0);
    } else if (progress.tier === 'breakout') {
      this.toasts.show('◈ Breakout scene — Level 3 venues unlocked, enemy pressure will rise!', 'success', 5.5);
    } else if (progress.tier === 'legend') {
      this.toasts.show('◈ Future legend status — the whole industry is watching.', 'success', 6.0);
    }

    trackBattleEvent('scene_tier_changed', {
      elapsed: this.elapsed,
      wave: this.waves.currentWaveIndex,
      tier: progress.tier,
      sceneScore: progress.sceneScore,
      peakReputation: progress.peakReputation,
      peakFanbase: progress.peakFanbase,
    });
    this.prevSceneTier = progress.tier;
  }

  /**
   * Returns the nearest claimed, upgradable venue (level < 3) to any unit in the
   * selection.  Used by the UPGRADE button to show cost and execute the upgrade.
   */
  private nearestClaimedVenueForUnits(units: Unit[]): VenueNode | null {
    const eligible = this.build.venues.filter(v => v.state === 'claimed' && v.venueLevel < 3);
    if (eligible.length === 0) return null;
    let best: VenueNode | null = null;
    let bestDist = Infinity;
    for (const unit of units) {
      if (!unit.alive || unit.isDowned) continue;
      for (const venue of eligible) {
        const d = Math.hypot(unit.gridPos.col - venue.col, unit.gridPos.row - venue.row);
        if (d < bestDist) { best = venue; bestDist = d; }
      }
    }
    return best;
  }

  private songHealRatio(): number {
    const dampening = Math.min(
      SIGNAL_JAMMER_MAX_HEAL_DAMPENING,
      this.activeSignalJammers() * SIGNAL_JAMMER_HEAL_DAMPENING,
    );
    return this.content.balance.goodBeaconHealRatio * (1 - dampening);
  }

  private rollNextGoodBeaconPulse(): number {
    const { goodBeaconPulseMin: min, goodBeaconPulseMax: max } = this.content.balance;
    return min + Math.random() * (max - min);
  }

  private healGoodForcesFromFutureSong(): void {
    const song = this.pickFutureSong();
    this.audio.playSnippet(song.audioUrl);
    let healed = 0;
    const healRatio = this.songHealRatio();
    for (const unit of this.units) {
      if (!unit.alive || unit.faction !== Faction.Player || unit.isDowned) continue;
      const before = unit.hp;
      unit.hp = Math.min(unit.maxHp, unit.hp + Math.ceil(unit.maxHp * healRatio));
      if (unit.hp > before) healed++;
    }
    this.mapRenderer.triggerPulse(0x37e0d0, 2.5);
    if (healed > 0) {
      const jammed = this.activeSignalJammers() > 0 ? ' partially jammed' : '';
      this.toasts.show(`♪ Future song signal: ${song.title}${jammed} — good forces restored!`, 'success', 4.5);
    }
  }

  private damageGoodForcesFromCapturedBeacon(): void {
    let fansLost = 0;
    let bandDowned = 0;
    let damaged = 0;

    for (const unit of this.units) {
      if (!unit.alive || unit.faction !== Faction.Player || unit.isDowned) continue;

      const damage = Math.max(1, Math.ceil(unit.maxHp * CAPTURED_BEACON_DAMAGE_RATIO));
      unit.hp = Math.max(0, unit.hp - damage);
      unit.hitFlash = 0.35;
      damaged++;

      if (unit.hp > 0) continue;

      if (unit.bandClass === 'fan') {
        unit.alive = false;
        unit.isMining = false;
        this.resources.onFanKilledByEnemy();
        fansLost++;
      } else if (unit.bandClass && unit.bandClass !== 'source') {
        this.downBandMemberFromBeacon(unit);
        bandDowned++;
      }
    }

    this.mapRenderer.triggerPulse(0xff2244, 3.0);
    if (fansLost > 0 || bandDowned > 0) {
      this.toasts.show(
        `⚠ Red Beacon pulse — ${fansLost} fans lost, ${bandDowned} band units neutralised!`,
        'danger',
        5.5,
      );
    } else if (damaged > 0) {
      this.toasts.show('⚠ Red Beacon pulse — good forces weakened!', 'warning', 4.0);
    } else {
      this.toasts.show('⚠ Red Beacon pulse — reclaim the Future Beacon!', 'warning', 4.0);
    }
  }

  private downBandMemberFromBeacon(unit: Unit): void {
    unit.hp = 0;
    unit.isDowned = true;
    unit.reviveProgress = 0;
    unit.path = [];
    unit.moveT = 0;
    unit.target = null;
    unit.attackCooldown = 0;
    unit.isMining = false;
    unit.clearCombatOrder();
    if (unit.bandClass === 'engine') {
      this.rhythm.onChrisKilled();
    }
  }

  private pickFutureSong(): FutureSong {
    if (this.nextFutureSong) {
      const song = this.nextFutureSong;
      this.nextFutureSong = null;
      return song;
    }
    return this.drawFutureSong();
  }

  private drawFutureSong(): FutureSong {
    const songs = this.content.futureSongs;
    if (songs.length === 0) return { title: 'Untitled Transmission' };
    if (this.futureSongBag.length === 0) {
      this.futureSongBag = [...songs];
    }
    const index = Math.floor(Math.random() * this.futureSongBag.length);
    const [song] = this.futureSongBag.splice(index, 1);
    return song ?? { title: 'Untitled Transmission' };
  }

  private updateFutureSongPrewarm(): void {
    if (this.nextFutureSong) return;

    const goodPulseSoon = (
      !this.hasBaddieBeacon() &&
      !this.beaconSystem.isEnemyControlled &&
      this.nextGoodBeaconPulse - this.goodBeaconPulseTimer <= 10
    );
    const songDropSoon = (
      this.beaconSystem.beaconOwner === 'player' &&
      this.beaconSystem.songDropProgress >= SONG_DROP_SUSTAIN_TIME - 10
    );

    if (!goodPulseSoon && !songDropSoon) return;

    const song = this.drawFutureSong();
    this.nextFutureSong = song;
    this.audio.warmSnippet(song.audioUrl);
  }

  private convertFansToBaddieWorkers(): void {
    const fans = this.units.filter(
      unit => unit.alive && !unit.isDowned && unit.faction === Faction.Player && unit.bandClass === 'fan',
    );
    if (fans.length === 0) {
      const beacon = this.units.find(u => u.alive && u.specialRole === 'baddieBeacon');
      if (beacon) {
        this.spawnBaddieBeaconGuards(beacon, BADDIE_BEACON_PULSE_GUARDS);
        this.spawnBaddieBeaconRepairCrews(beacon, BADDIE_BEACON_REPAIR_CREWS);
        this.mapRenderer.triggerPulse(0xff2244, 2.5);
        this.toasts.show('⚠ Baddie Beacon pulse — workers reinforced!', 'warning', 4.0);
      }
      return;
    }

    for (const fan of fans) {
      fan.faction = Faction.Enemy;
      fan.bandClass = null;
      fan.enemyType = 'GenZSwarm';
      fan.displayName = 'Baddie Worker';
      fan.isMining = false;
      fan.selected = false;
      fan.clearCombatOrder();
      fan.attackDamage = Math.max(fan.attackDamage, FAN_DAMAGE);
      fan.attackRate = Math.max(fan.attackRate, FAN_ATTACK_RATE);
      fan.sightRadius = Math.max(fan.sightRadius, FAN_SIGHT_RADIUS + 1);
      fan.attackRadius = Math.max(fan.attackRadius, FAN_ATTACK_RADIUS);
      fan.hitFlash = 0.25;
    }

    this.resources.fanbase = Math.max(0, this.resources.fanbase - fans.length);
    this.selection.setUnits(this.units);
    this.mapRenderer.triggerPulse(0xff2244, 2.5);
    this.toasts.show(`⚠ Baddie Beacon pulse — ${fans.length} fans converted into workers!`, 'danger', 5.5);
  }

  private spawnBaddieBeaconGuards(beacon: Unit, count: number): void {
    const offsets: [number, number][] = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [-1, 1], [1, -1], [-1, -1],
      [2, 0], [-2, 0], [0, 2], [0, -2],
    ];
    let spawned = 0;
    for (const [dc, dr] of offsets) {
      if (spawned >= count) break;
      const pos = { col: beacon.gridPos.col + dc, row: beacon.gridPos.row + dr };
      if (!this.map.inBounds(pos.col, pos.row) || !this.map.isPassable(pos)) continue;
      if (this.isTileOccupied(pos)) continue;
      this.spawnBaddieWorker(pos);
      spawned++;
    }
  }

  private repairBaddieBeacons(delta: number): void {
    const repairCrews = this.units.filter(
      unit => unit.alive && !unit.isDowned && unit.faction === Faction.Enemy && unit.displayName === 'Repair Crew',
    );
    if (repairCrews.length === 0) return;

    for (const beacon of this.units) {
      if (!beacon.alive || beacon.specialRole !== 'baddieBeacon' || beacon.hp >= beacon.maxHp) continue;
      const nearbyCrews = repairCrews.filter(
        crew => Math.hypot(crew.gridPos.col - beacon.gridPos.col, crew.gridPos.row - beacon.gridPos.row) <= BADDIE_BEACON_REPAIR_RANGE,
      );
      if (nearbyCrews.length === 0) continue;

      beacon.hp = Math.min(beacon.maxHp, beacon.hp + BADDIE_BEACON_REPAIR_RATE * nearbyCrews.length * delta);
      beacon.hitFlash = Math.max(beacon.hitFlash, 0.08);
      for (const crew of nearbyCrews) {
        crew.attackPulse = Math.max(crew.attackPulse, 0.08);
      }
      if (this.baddieBeaconRepairToastCooldown === 0) {
        this.baddieBeaconRepairToastCooldown = BADDIE_BEACON_REPAIR_TOAST_COOLDOWN;
        this.toasts.show('⚠ Repair Crew is restoring the Baddie Beacon!', 'warning', 3.5);
      }
    }
  }

  private spawnBaddieBeaconRepairCrews(beacon: Unit, count: number): void {
    const offsets: [number, number][] = [
      [2, 1], [-2, 1], [2, -1], [-2, -1],
      [3, 0], [-3, 0], [0, 3], [0, -3],
    ];
    let spawned = 0;
    for (const [dc, dr] of offsets) {
      if (spawned >= count) break;
      const pos = { col: beacon.gridPos.col + dc, row: beacon.gridPos.row + dr };
      if (!this.map.inBounds(pos.col, pos.row) || !this.map.isPassable(pos)) continue;
      if (this.isTileOccupied(pos)) continue;
      this.spawnRepairCrew(pos);
      spawned++;
    }
  }

  private spawnBaddieWorker(pos: GridPosition): Unit {
    const b = this.content.balance;
    const worker = new Unit();
    worker.gridPos = { ...pos };
    worker.faction = Faction.Enemy;
    worker.enemyType = 'GenZSwarm';
    worker.displayName = 'Baddie Worker';
    worker.threatScale = 0.9;
    worker.maxHp = Math.max(FAN_HP, Math.round(b.enemyHpSwarm * 0.9));
    worker.hp = worker.maxHp;
    worker.moveSpeed = b.enemyMoveSpeedSwarm;
    worker.attackDamage = Math.max(FAN_DAMAGE, b.enemyDamageSwarm);
    worker.attackRate = b.enemyAttackRate;
    worker.sightRadius = b.enemySightRadius + 1;
    worker.attackRadius = b.enemyAttackRadius;
    this.units.push(worker);
    this.unitRenderer.add(worker);
    return worker;
  }

  private spawnRepairCrew(pos: GridPosition): Unit {
    const b = this.content.balance;
    const worker = new Unit();
    worker.gridPos = { ...pos };
    worker.faction = Faction.Enemy;
    worker.enemyType = 'GenZSwarm';
    worker.displayName = 'Repair Crew';
    worker.threatScale = 0.82;
    worker.maxHp = Math.max(FAN_HP, Math.round(b.enemyHpSwarm * 0.75));
    worker.hp = worker.maxHp;
    worker.moveSpeed = b.enemyMoveSpeedSwarm * 0.95;
    worker.attackDamage = Math.max(1, Math.round(b.enemyDamageSwarm * 0.65));
    worker.attackRate = b.enemyAttackRate * 0.8;
    worker.sightRadius = b.enemySightRadius + 1;
    worker.attackRadius = b.enemyAttackRadius;
    this.units.push(worker);
    this.unitRenderer.add(worker);
    return worker;
  }

  // ── Autoplay (demo AI) ───────────────────────────────────────────────────

  private updateAutoplay(delta: number): void {
    this.autoplayTick += delta;
    if (this.autoplayTick < this.AUTOPLAY_INTERVAL) return;
    this.autoplayTick = 0;

    const alive = (u: Unit) => u.alive && !u.isDowned && u.faction === Faction.Player;
    const band  = this.units.filter(u => alive(u) && u.bandClass && u.bandClass !== 'fan');
    const fans  = this.units.filter(u => alive(u) && u.bandClass === 'fan');
    const combatFans = fans.filter(fan => fan.hp > fan.maxHp * 0.45);

    // ── Threat assessment ────────────────────────────────────────────────
    const bc = { col: this.map.beacon.col + 1, row: this.map.beacon.row + 1 };
    const enemies = this.units.filter(u =>
      u.alive && !u.isDowned && u.faction === Faction.Enemy && u.enemyType !== null,
    );
    const neutralPress = this.units.find(u =>
      u.alive &&
      !u.isDowned &&
      u.faction === Faction.Neutral &&
      u.enemyType === 'MusicPress',
    );
    const downed = this.units.filter(u => u.alive && u.isDowned && u.faction === Faction.Player);
    const rivalBeacon  = enemies.find(u => u.specialRole === 'baddieBeacon');
    const rivalVenue   = enemies.find(u => u.specialRole === 'rivalVenue');
    const monolith     = enemies.find(u => u.specialRole === 'streamingMonolith');
    const signalJammer = this.autoplayNearestUnit(enemies.filter(u => u.displayName === 'Signal Jammer'), bc);
    const repairCrew   = this.autoplayNearestUnit(enemies.filter(u => u.displayName === 'Repair Crew'), rivalBeacon?.gridPos ?? bc);
    const venueThreat  = this.autoplayVenueThreat(enemies);
    const enemyNearBeacon = this.autoplayNearestUnit(
      enemies.filter(u => Math.hypot(u.gridPos.col - bc.col, u.gridPos.row - bc.row) <= 14), bc,
    );
    const scout = this.autoplayNearestUnit(enemies.filter(u => u.displayName === 'A&R'), bc);
    const priorityTarget = rivalBeacon ?? rivalVenue ?? monolith ?? signalJammer ?? repairCrew ?? venueThreat ?? enemyNearBeacon ?? scout;

    // Ali handles the neutral Press as a conversion side objective. Other
    // units leave it alone and continue normal battle/economy priorities.
    const promoter = band.find(unit => unit.bandClass === 'promoter');
    if (neutralPress && promoter && promoter.path.length === 0 && !promoter.isDowned) {
      promoter.issueAttackTarget(neutralPress);
      this.autoplayIntent = 'electrasy: winning over the Music Press';
    }
    const battleBand = neutralPress && promoter ? band.filter(unit => unit !== promoter) : band;

    // ── Emergency: revive downed band members ────────────────────────────
    // Send ONE nearest idle unit; don't return so economy work still runs.
    if (downed.length > 0) {
      const target = downed[0];
      const helper = this.autoplayNearestUnit(
        [...band, ...fans].filter(u => u !== target && u.path.length === 0),
        target.gridPos,
      );
      if (helper) {
        const dest = this.autoplayFindPassableNear(target.gridPos, 1) ?? target.gridPos;
        helper.issueMove(this.pathfinding.findPath(helper.gridPos, dest), dest);
        this.autoplayIntent = `Claude: reviving ${target.displayName}`;
      }
    }

    // ── Combat: band + combat fans vs. priority target ───────────────────
    // Track fans committed to battle so the economy loop skips them.
    const fansInCombat = new Set<Unit>();
    if (priorityTarget) {
      const highPriority =
        priorityTarget.specialRole === 'baddieBeacon' ||
        priorityTarget.specialRole === 'rivalVenue' ||
        priorityTarget.specialRole === 'streamingMonolith' ||
        priorityTarget.displayName === 'Signal Jammer' ||
        priorityTarget.displayName === 'Repair Crew';
      const attackers = highPriority
        ? [...battleBand, ...combatFans]
        : battleBand.slice(0, Math.max(1, Math.min(2, battleBand.length)));
      for (const unit of attackers) {
        if (unit.path.length > 0 && unit.orderTargetUnit === priorityTarget) continue;
        unit.issueAttackTarget(priorityTarget);
        if (unit.bandClass === 'fan') fansInCombat.add(unit);
      }
      if (highPriority) {
        const supportFans = fans.filter(f => !attackers.includes(f)).slice(0, 2);
        for (const fan of supportFans) {
          if (fan.path.length > 0) continue;
          const dest = this.autoplayFindPassableNear(priorityTarget.gridPos, 3);
          if (dest) fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
          fansInCombat.add(fan);
        }
      }
      this.autoplayIntent = `Claude: focusing ${priorityTarget.displayName}`;
      // No early return — fan economy continues below regardless of combat.
    }

    // ── Band members: claim venues or patrol ─────────────────────────────
    const taken = new Set<string>();
    for (const unit of band) {
      if (unit.orderTarget && unit.path.length > 0)
        taken.add(`${unit.orderTarget.col},${unit.orderTarget.row}`);
    }
    for (const unit of band) {
      if (unit.path.length > 0) continue;
      const atVenue = this.build.venues.some(v =>
        (v.state === 'unclaimed' || v.state === 'claiming') &&
        Math.hypot(unit.gridPos.col - v.col, unit.gridPos.row - v.row) <= 2.0,
      );
      if (atVenue) continue;
      const venueTarget = this.build.venues
        .filter(v => v.state === 'unclaimed' && !taken.has(`${v.col},${v.row}`))
        .map(v => ({ v, d: Math.hypot(unit.gridPos.col - v.col, unit.gridPos.row - v.row) }))
        .sort((a, b) => a.d - b.d)[0]?.v;
      if (venueTarget) {
        taken.add(`${venueTarget.col},${venueTarget.row}`);
        const dest = this.autoplayVenueDestination(venueTarget, unit);
        if (dest) {
          unit.issueMove(this.pathfinding.findPath(unit.gridPos, dest), dest);
          if (!priorityTarget) this.autoplayIntent = `Claude: claiming ${venueTarget.name}`;
        }
      } else {
        const patrol = this.autoplayFindPassableNear(bc, 6 + Math.floor(Math.random() * 4));
        if (patrol) unit.issueAttackMove(this.pathfinding.findPath(unit.gridPos, patrol), patrol);
        if (!priorityTarget) this.autoplayIntent = 'Claude: patrolling Beacon routes';
      }
    }

    // ── Fans: economy — always runs, even during combat ──────────────────
    // Fans not committed to battle are sent to productive economy tasks.
    // Priority order: free venue claiming → speed up active claim →
    //   build workshop → mine gear (round-robins across all depots).
    const claimingVenues  = this.build.venues.filter(v => v.state === 'claiming');
    const unclaimedVenues = this.build.venues.filter(v => v.state === 'unclaimed' && !taken.has(`${v.col},${v.row}`));
    const incompleteSites = this.build.buildSites.filter(s => !s.completed);
    const depots = this.gearSystem.nodes;
    let depotIdx = 0;

    for (const fan of fans) {
      if (fansInCombat.has(fan)) continue;
      if (fan.path.length > 0) continue;

      // 1. Fans claim unclaimed venues for free — leverages a unique fan perk.
      if (unclaimedVenues.length > 0) {
        const entry = unclaimedVenues
          .filter(v => !taken.has(`${v.col},${v.row}`))
          .map(v => ({ v, d: Math.hypot(fan.gridPos.col - v.col, fan.gridPos.row - v.row) }))
          .filter(x => x.d < 28)
          .sort((a, b) => a.d - b.d)[0];
        if (entry) {
          const dest = this.autoplayVenueDestination(entry.v, fan);
          if (dest) {
            taken.add(`${entry.v.col},${entry.v.row}`);
            fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
            if (!priorityTarget) this.autoplayIntent = `Claude: fans claiming ${entry.v.name}`;
            continue; // skip lower-priority tasks only when a move was issued
          }
          // dest null → fall through to gear mining
        }
      }

      // 2. Speed up an active venue claim.
      if (claimingVenues.length > 0) {
        const entry = claimingVenues
          .map(v => ({ v, d: Math.hypot(fan.gridPos.col - v.col, fan.gridPos.row - v.row) }))
          .sort((a, b) => a.d - b.d)[0];
        if (entry.d < 22) {
          const dest = this.autoplayVenueDestination(entry.v, fan);
          if (dest) {
            fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
            if (!priorityTarget) this.autoplayIntent = `Claude: fans boosting ${entry.v.name}`;
            continue;
          }
          // dest null → fall through to gear mining
        }
      }

      // 3. Deposit gear at an incomplete build site.
      if (incompleteSites.length > 0 && this.resources.gear > 0) {
        const site = incompleteSites
          .map(s => ({ s, d: Math.hypot(fan.gridPos.col - s.col, fan.gridPos.row - s.row) }))
          .sort((a, b) => a.d - b.d)[0]?.s;
        if (site) {
          fan.issueMove(
            this.pathfinding.findPath(fan.gridPos, { col: site.col, row: site.row }),
            { col: site.col, row: site.row },
          );
          if (!priorityTarget) this.autoplayIntent = `Claude: fans building ${site.name}`;
          continue;
        }
      }

      // 4. Mine gear — distribute fans round-robin across all depots.
      if (depots.length > 0) {
        const depot = depots[depotIdx % depots.length];
        depotIdx++;
        const dest = this.autoplayFindPassableNear({ col: depot.col, row: depot.row }, 2)
          ?? { col: depot.col, row: depot.row };
        fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
        if (!priorityTarget) this.autoplayIntent = 'Claude: fans mining gear';
      }
    }

    // ── Upgrade claimed venues when resources allow ───────────────────────
    if (!priorityTarget) {
      for (const venue of this.build.venues) {
        if (venue.state !== 'claimed' || venue.venueLevel >= 3) continue;
        const result = this.build.upgradeVenue(venue.id);
        if (result.success) {
          this.toasts.show(`◈ ${venue.name} upgraded to Level ${venue.venueLevel}!`, 'success', 3.5);
          this.autoplayIntent = `Claude: upgrading ${venue.name}`;
          break;
        }
      }
    }
  }

  private autoplayNearestUnit(units: Unit[], target: GridPosition): Unit | null {
    return units
      .map(unit => ({
        unit,
        d: Math.hypot(unit.gridPos.col - target.col, unit.gridPos.row - target.row),
      }))
      .sort((a, b) => a.d - b.d)[0]?.unit ?? null;
  }

  private autoplayVenueThreat(enemies: Unit[]): Unit | null {
    const threatenedVenues = this.build.venues.filter(v => v.state === 'claimed' || v.state === 'claiming');
    let best: { unit: Unit; d: number } | null = null;
    for (const enemy of enemies) {
      for (const venue of threatenedVenues) {
        const d = Math.hypot(enemy.gridPos.col - venue.col, enemy.gridPos.row - venue.row);
        if (d > 6) continue;
        if (!best || d < best.d) best = { unit: enemy, d };
      }
    }
    return best?.unit ?? null;
  }

  /** Return a random passable tile within `radius` of `centre`, or null. */
  private autoplayFindPassableNear(centre: { col: number; row: number }, radius: number): { col: number; row: number } | null {
    const candidates: { col: number; row: number }[] = [];
    for (let dc = -radius; dc <= radius; dc++) {
      for (let dr = -radius; dr <= radius; dr++) {
        const pos = { col: centre.col + dc, row: centre.row + dr };
        if (Math.hypot(dc, dr) > radius) continue;
        if (!this.map.inBounds(pos.col, pos.row)) continue;
        if (!this.map.isPassable(pos)) continue;
        if (this.isTileOccupied(pos)) continue;
        candidates.push(pos);
      }
    }
    if (candidates.length === 0) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  private autoplayVenueDestination(venue: VenueNode, unit: Unit): GridPosition | null {
    const direct = { col: venue.col, row: venue.row };
    if (Math.hypot(unit.gridPos.col - venue.col, unit.gridPos.row - venue.row) <= VENUE_CLAIM_RANGE)
      return direct;

    if (this.map.inBounds(direct.col, direct.row) && this.map.isPassable(direct)
        && !this.isTileOccupied(direct, unit))
      return direct;

    // Access tile occupied — find nearest passable unoccupied tile within claim range.
    // No pathfinding here: running findPath per-candidate burns the frame budget when
    // multiple units call this in the same tick. The movement system handles re-routing.
    let best: GridPosition | null = null;
    let bestDist = Infinity;
    const radius = Math.ceil(VENUE_CLAIM_RANGE);
    for (let dc = -radius; dc <= radius; dc++) {
      for (let dr = -radius; dr <= radius; dr++) {
        if (Math.hypot(dc, dr) > VENUE_CLAIM_RANGE) continue;
        const pos = { col: venue.col + dc, row: venue.row + dr };
        if (!this.map.inBounds(pos.col, pos.row)) continue;
        if (!this.map.isPassable(pos)) continue;
        if (this.isTileOccupied(pos, unit)) continue;
        const d = Math.hypot(unit.gridPos.col - pos.col, unit.gridPos.row - pos.row);
        if (d < bestDist) { bestDist = d; best = pos; }
      }
    }
    return best;
  }

  // ── Song Drop ───────────────────────────────────────────────────────────

  /**
   * Fire the Song Drop when the signal sustain threshold is hit.
   * Effects: resource burst, anthem speed buff on all friendlies, stun
   * on nearby enemies.
   */
  private activateSongDrop(): void {
    this.beaconSystem.consumeSongDrop();
    this.songDropCount++;

    // Resource burst
    this.resources.add('reputation', SONG_DROP_REP_BONUS);
    this.resources.add('sales',      SONG_DROP_SALES_BONUS);
    this.resources.add('gear',       SONG_DROP_GEAR_BONUS);

    // Anthem speed buff — all living friendly units
    for (const unit of this.units) {
      if (unit.alive && unit.faction === Faction.Player) {
        unit.anthemBuff = SONG_DROP_ANTHEM_SECS;
      }
    }

    // Heal pulse — restore HP to all living friendlies; revive downed band members
    const healRatio = this.songHealRatio();
    for (const unit of this.units) {
      if (!unit.alive || unit.faction !== Faction.Player) continue;
      if (unit.isDowned) {
        // Song drop mercy-revives downed band members at half the heal ratio
        unit.isDowned      = false;
        unit.reviveProgress = 0;
        unit.hp = Math.max(1, Math.ceil(unit.maxHp * healRatio * 0.5));
      } else {
        unit.hp = Math.min(unit.maxHp, unit.hp + Math.ceil(unit.maxHp * healRatio));
      }
    }

    // Visual: signal ring expands across the map
    this.mapRenderer.triggerPulse(0x37e0d0, 3.0);

    // Stun nearby enemies (within BEACON_NEAR_RADIUS of the beacon centre)
    const bcCol = this.map.beacon.col + 0.5;
    const bcRow = this.map.beacon.row + 0.5;
    for (const unit of this.units) {
      if (unit.alive && unit.faction === Faction.Enemy) {
        const d = Math.hypot(unit.gridPos.col - bcCol, unit.gridPos.row - bcRow);
        if (d <= BEACON_NEAR_RADIUS) {
          unit.stunTimer = SONG_DROP_STUN_SECS;
        }
      }
    }

    // Per-venue fanbase burst: each claimed venue draws extra fans to the cause
    const claimedVenues = this.build.venues.filter(v => v.state === 'claimed');
    const fansGained    = claimedVenues.length * SONG_DROP_FANS_PER_VENUE;
    if (fansGained > 0) {
      this.resources.add('fanbase', fansGained);
    }

    // Industry rages and retaliates — Song Drop accelerates their next wave
    this.waves.triggerRage();
    this.toasts.show('◈ Industry: Signal detected — retaliating!', 'warning', 3.5);

    const song = this.pickFutureSong();
    this.audio.playSnippet(song.audioUrl);
    const jammed = this.activeSignalJammers() > 0 ? ' partially jammed' : '';
    const venueBoost = fansGained > 0 ? ` +${fansGained} fans` : '';
    this.toasts.show(`♪ SIGNAL SURGE — ${song.title} received from the future${jammed}!${venueBoost}`, 'success', 5.0);
  }

  // ── Game Over ───────────────────────────────────────────────────────────

  private areGoodForcesNeutralised(): boolean {
    let hasFanAlive = false;
    let hasBandActive = false;
    let hasBandUnit = false;

    for (const unit of this.units) {
      if (unit.faction !== Faction.Player) continue;

      if (unit.bandClass === 'fan') {
        if (unit.alive && !unit.isDowned) hasFanAlive = true;
        continue;
      }

      if (unit.bandClass && unit.bandClass !== 'source') {
        hasBandUnit = true;
        if (unit.alive && !unit.isDowned && unit.hp > 0) hasBandActive = true;
      }
    }

    return hasBandUnit && !hasFanAlive && !hasBandActive;
  }

  /**
   * Freeze the simulation and show the Game Over screen.
   * Called once all fans are gone and all band members are neutralised.
   */
  private triggerGameOver(): void {
    this.gameState = 'paused'; // freeze simulation while keeping toasts alive
    const progression = this.resources.progression;
    const stats: GameStats = {
      sceneScore:      this.computeFinalSceneScore(),
      sceneTier:       progression.label,
      timeSurvived:    this.elapsed,
      wavesRepelled:   this.waves.currentWaveIndex,
      enemiesDefeated: this.enemiesDefeated,
      venuesClaimed:   this.build.venues.filter(v => v.state === 'claimed').length,
      songDrops:       this.songDropCount,
      mapCode:         this.map.mapCode,
    };
    trackBattleEvent('game_over', {
      ...stats,
      outcome: 'lost',
      lossReason: this.gameOverLossReason(),
      corporateHQDestroyed: this.corporateHQDestroyed,
      beaconOwner: this.beaconSystem.beaconOwner,
      enemyBeaconControlTime: Math.round(this.enemyBeaconControlTime),
      mapSeed: this.map.seed,
      mapCode: this.map.mapCode,
    });
    void flushUnitPositions(battleSessionId(), this.positionSamples);
    this.gameOverScreen.show(this.app.screen.width, this.app.screen.height, stats);
  }

  private gameOverLossReason(): string {
    const fansAlive = this.units.some(u => u.alive && !u.isDowned && u.faction === Faction.Player && u.bandClass === 'fan');
    const activeBand = this.units.some(
      u => u.alive && !u.isDowned && u.hp > 0 && u.faction === Faction.Player && u.bandClass && u.bandClass !== 'fan' && u.bandClass !== 'source',
    );
    if (!fansAlive && !activeBand) return 'all_good_forces_neutralised';
    if (this.beaconSystem.beaconOwner === 'enemy') return 'beacon_control_lost';
    return 'unknown';
  }

  private computeFinalSceneScore(): number {
    const progression = this.resources.progression;
    const claimed = this.build.venues.filter(v => v.state === 'claimed');
    const venueScore = claimed.reduce((sum, venue) => sum + 50 + venue.venueLevel * 25, 0);
    return Math.floor(
      progression.sceneScore +
      venueScore +
      this.enemiesDefeated * 12 +
      this.songDropCount * 150 +
      this.waves.currentWaveIndex * 75 +
      this.elapsed / 4,
    );
  }

  // ── Codex AI Director ────────────────────────────────────────────────────

  private static readonly CODEX_TAUNTS: Partial<Record<string, string>> = {
    bleedEconomy:        '◈ Industry: "Starve the fanbase. Drain their economy."',
    drainSignal:         '◈ Industry: "Silence the Beacon. No signal, no movement."',
    captureBeacon:       '◈ Industry: "The Future Beacon will fall today."',
    denySongDrop:        '◈ Industry: "That anthem drops over our dead bodies."',
    raidExpansion:       '◈ Industry: "Block their fanbase before it takes root."',
    huntRecruiters:      '◈ Industry: "Take out the recruiter. Their ranks stop growing."',
    pressureRecruitment: '◈ Industry: "Destroy the rehearsal space. No more recruits."',
    stallSongDrop:       '◈ Industry: "Stall them. The signal will falter."',
    breakDefence:        '◈ Industry: "Their lines are thick. But not unbreakable."',
    splitAttention:      '◈ Industry: "Two fronts. They cannot cover both."',
    feintThenSpike:      '◈ Industry: "Feint north... then strike from the south."',
  };

  private buildBattleSnapshot(): BattleSnapshot {
    const res = this.resources.snapshot;
    return {
      tick:  Math.floor(this.elapsed),
      wave:  this.waves.currentWaveIndex,
      map:   { width: this.map.width, height: this.map.height },
      beacon: {
        signal:          this.beaconSystem.signal,
        hp:              this.beacon.hp,
        captureProgress: this.beaconSystem.captureProgress,
        songDropProgress: this.beaconSystem.songDropProgress,
      },
      resources: {
        enemyBudget: 200 + this.waves.currentWaveIndex * 150,
        reputation:  res.reputation,
        sales:       res.sales,
        gear:        res.gear,
        fanbase:     res.fanbase,
        fanbaseCap:  res.fanbaseCap,
      },
      visibility: { edgeVisibility: this.buildEdgeVisibility() },
      enemyUnits: this.units
        .filter(u => u.alive && u.faction === Faction.Enemy)
        .map(u => ({
          id:   u.id,
          type: u.enemyType ?? 'GenZSwarm',
          col:  u.gridPos.col,
          row:  u.gridPos.row,
          hp:   u.hp,
          maxHp: u.maxHp,
        })),
      playerUnits: this.units
        .filter(u => u.alive && u.faction === Faction.Player)
        .map(u => ({
          id:       u.id,
          type:     u.bandClass ?? 'fan',
          col:      u.gridPos.col,
          row:      u.gridPos.row,
          hp:       u.hp,
          maxHp:    u.maxHp,
          isMining: u.isMining,
        })),
      buildings: [
        {
          id:    'futureBeacon',
          type:  'FutureBeacon',
          col:   this.map.beacon.col,
          row:   this.map.beacon.row,
          hp:    this.beacon.hp,
          maxHp: this.beacon.maxHp ?? 100,
        },
        ...this.build.venues.map(v => ({
          id:    v.id,
          type:  venueIdToBuildingType(v.id),
          col:   v.col,
          row:   v.row,
          hp:    v.hp,
          maxHp: v.maxHp,
        })),
        ...(this.corporateHQDestroyed ? [] : [{
          id:    'corporateHQ',
          type:  'CorporateHQ',
          col:   this.map.corporateHQ.buildingCol,
          row:   this.map.corporateHQ.buildingRow,
          hp:    this.units.find(u => u.specialRole === 'corporateHQ')?.hp ?? 0,
          maxHp: CORPORATE_HQ_HP,
        }]),
        ...this.units
          .filter(u => u.alive && u.specialRole === 'rivalVenue')
          .map(u => ({
            id:    u.id,
            type:  u.constructionComplete ? 'RivalVenue' : 'RivalVenueConstruction',
            col:   u.gridPos.col,
            row:   u.gridPos.row,
            hp:    u.hp,
            maxHp: u.maxHp,
            level: u.rivalVenueLevel,
            age:   Math.round(u.rivalVenueAge),
            spawnPressure: u.constructionComplete
              ? Number((this.rivalVenueSpawnMix(u).length / this.rivalVenueSpawnInterval(u)).toFixed(3))
              : 0,
          })),
      ],
      venues: this.build.venues.map(v => ({
        id:    v.id,
        type:  venueIdToBuildingType(v.id),
        col:   v.col,
        row:   v.row,
        hp:    v.hp,
        maxHp: v.maxHp,
        state: v.state,
      })),
    };
  }

  /** Compute how visible each spawn edge is to the player (0=unseen, 1=fully watched). */
  private buildEdgeVisibility(): Partial<Record<SpawnEdge, number>> {
    const edges = this.buildPlannerEdgeTiles();
    const result: Partial<Record<SpawnEdge, number>> = {};
    for (const edge of ['north', 'south', 'east', 'west'] as SpawnEdge[]) {
      const tiles = edges[edge];
      const visible = tiles.filter(t => this.fog.getState(t.col, t.row) === FogState.Visible).length;
      result[edge] = tiles.length > 0 ? visible / tiles.length : 0;
    }
    return result;
  }

  /**
   * Estimate the same playable spawn rim WaveSystem uses: scan inwards from
   * each map edge and keep the first passable tile per column/row. This keeps
   * Codex edge-visibility scoring meaningful on large maps with fog margins.
   */
  private buildPlannerEdgeTiles(): Record<SpawnEdge, GridPosition[]> {
    const { width, height } = this.map;
    const scanDepth = Math.max(width, height);
    const edges: Record<SpawnEdge, { col: number; row: number }[]> = {
      north: [], south: [], east: [], west: [],
    };

    for (let col = 2; col < width - 2; col++) {
      for (let row = 0; row < scanDepth; row++) {
        const pos = { col, row };
        if (this.isPlannerEdgeTile(pos)) { edges.north.push(pos); break; }
      }
      for (let row = height - 1; row >= height - scanDepth; row--) {
        const pos = { col, row };
        if (this.isPlannerEdgeTile(pos)) { edges.south.push(pos); break; }
      }
    }

    for (let row = 2; row < height - 2; row++) {
      for (let col = 0; col < scanDepth; col++) {
        const pos = { col, row };
        if (this.isPlannerEdgeTile(pos)) { edges.west.push(pos); break; }
      }
      for (let col = width - 1; col >= width - scanDepth; col--) {
        const pos = { col, row };
        if (this.isPlannerEdgeTile(pos)) { edges.east.push(pos); break; }
      }
    }

    return edges;
  }

  private isPlannerEdgeTile(pos: GridPosition): boolean {
    if (!this.map.inBounds(pos.col, pos.row) || !this.map.isPassable(pos)) return false;
    const hq = this.map.corporateHQ;
    const nearHQ = Math.hypot(pos.col - hq.buildingCol, pos.row - hq.buildingRow) <= CORPORATE_HQ_EDGE_SPAWN_EXCLUSION_RADIUS;
    const corporateApproach = pos.col >= hq.accessCol - 16 && pos.row <= hq.accessRow + 45;
    return !nearHQ && !corporateApproach;
  }

  /**
   * Process EnemyCommand[] from BattlePlanner.decide():
   *   move     — direct a unit toward a BattlePlanner-chosen position
   *   retreat  — mark a low-HP unit to flee toward the map edge
   *   spawn    — spawn a bonus enemy unit from a given edge
   *   priority — mark a high-value player target and assign nearby attackers
   */
  private applyCodexCommands(commands: ReturnType<EnemyAI['decide']>): void {
    let planToast = '';
    for (const cmd of commands) {
      switch (cmd.type) {
        case 'move': {
          const unit = this.units.find(u => u.id === cmd.unitId && u.alive);
          if (unit) unit.commandTarget = { col: cmd.target.col, row: cmd.target.row };
          break;
        }
        case 'retreat': {
          const unit = this.units.find(u => u.id === cmd.unitId && u.alive);
          if (unit) { unit.isRetreating = true; unit.commandTarget = null; }
          break;
        }
        case 'spawn': {
          // Codex spawns a bonus unit — capped to avoid flooding in early waves
          if (this.waves.currentWaveIndex >= 3) {
            this.waves.spawnForCodex(cmd.enemyType, cmd.fromEdge);
          }
          break;
        }
        case 'priority': {
          this.applyCodexPriority(cmd.targetId, cmd.priority);
          // Harvest the plan name from BattlePlanner's last decision for the toast
          // (BattlePlanner emits one priority command per plan with the key target)
          if (!planToast) {
            // Try to infer plan from priority target type — use a generic taunt
            planToast = '◈ Industry is redirecting forces…';
          }
          break;
        }
      }
    }

    // Show a Codex taunt toast when a plan is issued (fire at most once per decide cycle)
    const planName = this.inferLastPlan(commands);
    const taunt = planName
      ? (Game.CODEX_TAUNTS[planName] ?? '◈ Industry is adjusting tactics…')
      : null;
    if (taunt) this.toasts.show(taunt, 'warning', 4.0);
  }

  private applyCodexPriority(targetId: string, priority: 'high' | 'low'): void {
    if (priority !== 'high') return;
    const target = this.units.find(unit =>
      unit.id === targetId &&
      unit.alive &&
      !unit.isDowned &&
      unit.faction === Faction.Player,
    );
    if (!target) return;

    const attackers = this.units
      .filter(unit =>
        unit.alive &&
        !unit.isDowned &&
        unit.faction === Faction.Enemy &&
        unit.enemyType !== null &&
        unit.specialRole !== 'baddieBeacon' &&
        unit.specialRole !== 'corporateHQ' &&
        unit.specialRole !== 'rivalVenue' &&
        unit.specialRole !== 'rivalVenueBuilder' &&
        unit.displayName !== 'Repair Crew' &&
        unit.displayName !== 'Monolith Guard',
      )
      .sort((a, b) => {
        const da = Math.hypot(a.gridPos.col - target.gridPos.col, a.gridPos.row - target.gridPos.row);
        const db = Math.hypot(b.gridPos.col - target.gridPos.col, b.gridPos.row - target.gridPos.row);
        return da - db;
      })
      .slice(0, 4);

    for (const attacker of attackers) {
      attacker.commandTarget = null;
      attacker.issueAttackTarget(target);
    }
  }

  /**
   * Infer the active strategic plan from the command list.
   * BattlePlanner always emits a 'priority' command pointing at its chosen target
   * when a plan is active — we use its presence as a plan-active signal and map
   * the accompanying 'move' target back to a plan name.
   */
  private inferLastPlan(commands: ReturnType<EnemyAI['decide']>): string | null {
    const hasPriority = commands.some(c => c.type === 'priority');
    if (!hasPriority) return null;
    const hasSpawn = commands.some(c => c.type === 'spawn');
    if (!hasSpawn) return null;
    const spawn = commands.find(c => c.type === 'spawn');
    if (!spawn || spawn.type !== 'spawn') return null;
    const plansByType: Record<string, string> = {
      GenZSwarm:      'bleedEconomy',
      StreamingMogul: 'drainSignal',
      TributeAct:     'captureBeacon',
      BadManager:     'huntRecruiters',
      FilePirate:     'raidExpansion',
      RivalVenueBuilder: 'raidExpansion',
    };
    return plansByType[spawn.enemyType] ?? null;
  }

  // ── Save / Load ─────────────────────────────────────────────────────────

  /**
   * Quick parse of localStorage to extract only the map seed, without fully
   * validating the save.  Used before TileMap is constructed so the map can
   * be regenerated from the same seed that was active when the game was saved.
   * Returns undefined when there is no save or the seed field is missing.
   */
  private peekSaveMapSeed(): number | undefined {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return undefined;
      const partial = JSON.parse(raw) as Partial<SaveState>;
      if (partial.mapWidth !== this.map.width || partial.mapHeight !== this.map.height) return undefined;
      return typeof partial?.mapSeed === 'number' ? partial.mapSeed : undefined;
    } catch {
      return undefined;
    }
  }

  /** Serialize the full game state to localStorage. */
  private saveGame(): void {
    const state: SaveState = {
      version:         1,
      savedAt:         Date.now(),
      mapSeed:         this.map.seed,
      mapWidth:        this.map.width,
      mapHeight:       this.map.height,
      elapsed:         this.elapsed,
      enemiesDefeated: this.enemiesDefeated,
      songDropCount:   this.songDropCount,
      goodBeaconPulseTimer: this.goodBeaconPulseTimer,
      nextGoodBeaconPulse: this.nextGoodBeaconPulse,
      baddieBeaconPulseTimer: this.baddieBeaconPulseTimer,
      capturedBeaconPulseTimer: this.capturedBeaconPulseTimer,
      corporateHQSpawnTimer: this.corporateHQSpawnTimer,
      corporateHQSecurityTimer: this.corporateHQSecurityTimer,
      corporateHQHintPulseTimer: this.corporateHQHintPulseTimer,
      corporateHQDiscovered: this.corporateHQDiscovered,
      corporateHQDestroyed: this.corporateHQDestroyed,
      centralBeaconOwner: this.beaconSystem.beaconOwner,
      centralBeaconCaptureTimer: this.beaconSystem.getState().captureTimer,
      resources:       this.resources.snapshot,
      beaconSignal:    this.beaconSystem.signal,
      waves:           this.waves.getState(),
      spells:          this.spells.getState(),
      units: this.units.map(u => ({
        id:                u.id,
        col:               u.gridPos.col,
        row:               u.gridPos.row,
        faction:           u.faction,
        bandClass:         u.bandClass,
        enemyType:         u.enemyType,
        isHero:            u.isHero,
        displayName:       u.displayName,
        threatScale:       u.threatScale,
        specialRole:       u.specialRole,
        objectiveTarget:    u.objectiveTarget ? { ...u.objectiveTarget } : null,
        constructionProgress: u.constructionProgress,
        constructionComplete: u.constructionComplete,
        isConstructing:    u.isConstructing,
        linkedUnitId:      u.linkedUnitId,
        spawnTimer:        u.spawnTimer,
        rivalVenueLevel:   u.rivalVenueLevel,
        rivalVenueAge:     u.rivalVenueAge,
        rivalVenueNextEscalation: u.rivalVenueNextEscalation,
        maxHp:             u.maxHp,
        hp:                u.hp,
        isDowned:          u.isDowned,
        reviveProgress:    u.reviveProgress,
        moveSpeed:         u.moveSpeed,
        attackDamage:      u.attackDamage,
        attackRate:        u.attackRate,
        sightRadius:       u.sightRadius,
        attackRadius:      u.attackRadius,
        pressHostileTimer: u.pressHostileTimer,
      })),
      venues: this.build.venues.map(v => ({
        id:             v.id,
        name:           v.name,
        venueType:      v.venueType,
        col:            v.col,
        row:            v.row,
        buildingCol:    v.buildingCol,
        buildingRow:    v.buildingRow,
        state:          v.state,
        progress:       v.progress,
        hp:             v.hp,
        repBonus:       v.repBonus,
        salesBonus:     v.salesBonus,
        fanBonus:       v.fanBonus,
        gearBonus:      v.gearBonus,
        claimCostRep:   v.claimCostRep,
        claimCostSales: v.claimCostSales,
      })),
      buildSites: this.build.buildSites.map(s => ({
        id:           s.id,
        gearProgress: s.gearProgress,
        completed:    s.completed,
      })),
    };
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(state));
    } catch {
      this.toasts.show('⚠ Save failed (storage full?)', 'danger', 4.0);
    }
  }

  /**
   * Deserialize a save from localStorage and restore all game state.
   * Returns true if a valid save was found and applied; false otherwise.
   * Must be called AFTER all systems are constructed but BEFORE any units
   * are spawned — it creates the units itself.
   */
  private loadGame(): boolean {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return false;

    let state: SaveState;
    try {
      state = JSON.parse(raw) as SaveState;
    } catch {
      return false;
    }
    if (state?.version !== 1) return false;
    if (state.mapWidth !== this.map.width || state.mapHeight !== this.map.height) return false;

    // ── Stats ────────────────────────────────────────────────────────────────
    this.elapsed         = state.elapsed;
    this.enemiesDefeated = state.enemiesDefeated;
    this.songDropCount   = state.songDropCount;
    this.goodBeaconPulseTimer = state.goodBeaconPulseTimer ?? 0;
    this.nextGoodBeaconPulse = state.nextGoodBeaconPulse ?? this.rollNextGoodBeaconPulse();
    this.baddieBeaconPulseTimer = state.baddieBeaconPulseTimer ?? 0;
    this.capturedBeaconPulseTimer = state.capturedBeaconPulseTimer ?? 0;
    this.corporateHQSpawnTimer = state.corporateHQSpawnTimer ?? 0;
    this.corporateHQSecurityTimer = state.corporateHQSecurityTimer ?? 0;
    this.corporateHQHintPulseTimer = state.corporateHQHintPulseTimer ?? 0;
    this.corporateHQDiscovered = state.corporateHQDiscovered ?? false;
    this.corporateHQDestroyed = state.corporateHQDestroyed ?? false;

    // ── Resources ────────────────────────────────────────────────────────────
    this.resources.reputation = state.resources.reputation;
    this.resources.fanbase    = state.resources.fanbase;
    this.resources.fanbaseCap = state.resources.fanbaseCap;
    this.resources.sales      = state.resources.sales;
    this.resources.gear       = state.resources.gear;
    this.resources.peakReputation = state.resources.peakReputation ?? state.resources.reputation;
    this.resources.peakFanbase    = state.resources.peakFanbase ?? state.resources.fanbase;
    this.resources.sceneScore     = state.resources.sceneScore ?? 0;
    this.resources.restoreBadReview(state.resources.badReviewRemaining ?? 0);
    this.resources.syncProgressionPeaks();
    this.prevSceneTier = this.resources.progression.tier;

    // ── Beacon ───────────────────────────────────────────────────────────────
    this.beacon.signalStrength = state.beaconSignal;
    this.beaconSystem.restoreState({
      owner: state.centralBeaconOwner,
      captureTimer: state.centralBeaconCaptureTimer,
    });
    this.prevBeaconOwner = this.beaconSystem.beaconOwner;

    // ── Waves ─────────────────────────────────────────────────────────────────
    this.waves.restoreState(state.waves);

    // ── Spells ────────────────────────────────────────────────────────────────
    if (state.spells) this.spells.restoreState(state.spells);

    // ── Units ─────────────────────────────────────────────────────────────────
    for (const su of state.units) {
      const unit             = new Unit(su.id);
      unit.gridPos           = { col: su.col, row: su.row };
      unit.faction           = su.faction;
      unit.bandClass         = su.bandClass as BandClass | null;
      unit.enemyType         = su.enemyType as EnemyType | null;
      unit.isHero            = su.isHero;
      unit.displayName       = su.displayName;
      unit.threatScale       = su.threatScale ?? 1;
      unit.specialRole       = su.specialRole ?? null;
      unit.objectiveTarget   = su.objectiveTarget ? { ...su.objectiveTarget } : null;
      unit.constructionProgress = su.constructionProgress ?? 0;
      unit.constructionComplete = su.constructionComplete ?? false;
      unit.isConstructing    = su.isConstructing ?? false;
      unit.linkedUnitId      = su.linkedUnitId ?? null;
      unit.spawnTimer        = su.spawnTimer ?? 0;
      unit.rivalVenueLevel   = su.rivalVenueLevel ?? 1;
      unit.rivalVenueAge     = su.rivalVenueAge ?? 0;
      unit.rivalVenueNextEscalation = su.rivalVenueNextEscalation
        ?? nextRivalVenueEscalationTime(unit.rivalVenueLevel);
      unit.maxHp             = su.maxHp;
      unit.hp                = su.hp;
      unit.isDowned          = su.isDowned ?? false;
      unit.reviveProgress    = su.reviveProgress ?? 0;
      unit.moveSpeed         = su.moveSpeed;
      unit.attackDamage      = su.attackDamage;
      unit.attackRate        = su.attackRate;
      unit.sightRadius       = su.sightRadius;
      unit.attackRadius      = su.attackRadius;
      unit.pressHostileTimer = su.pressHostileTimer;
      this.units.push(unit);
      this.unitRenderer.add(unit);
    }
    this.selection.setUnits(this.units);

    // ── Venues ────────────────────────────────────────────────────────────────
    for (const sv of state.venues) {
      if (ORIGINAL_VENUE_IDS.has(sv.id)) {
        // Restore mutable state onto the pre-existing venue object
        const venue = this.build.venues.find(v => v.id === sv.id);
        if (venue) {
          venue.state      = sv.state as 'unclaimed' | 'claiming' | 'claimed';
          venue.progress   = sv.progress;
          venue.hp         = sv.hp;
          venue.engagement = 1.0; // reset on load; player re-engages naturally
        }
      } else {
        // Fan-built venue — reconstruct fully and push into the venues array
        this.build.venues.push({
          id: sv.id, name: sv.name,
          venueType: sv.venueType ?? 'workshop',
          col: sv.col, row: sv.row,
          buildingCol: sv.buildingCol, buildingRow: sv.buildingRow,
          state: sv.state as 'unclaimed' | 'claiming' | 'claimed',
          progress: sv.progress,
          hp: sv.hp, maxHp: VENUE_HP_MAX,
          claimerUnit: null,
          repBonus: sv.repBonus, salesBonus: sv.salesBonus,
          fanBonus: sv.fanBonus, gearBonus: sv.gearBonus,
          baseRepBonus:   sv.repBonus,
          baseSalesBonus: sv.salesBonus,
          baseFanBonus:   sv.fanBonus,
          baseGearBonus:  sv.gearBonus,
          venueLevel:     1,
          claimCostRep: sv.claimCostRep, claimCostSales: sv.claimCostSales,
          isUnderAttack: false,
          engagement:     1.0,
        });
      }
    }

    // ── Build sites ───────────────────────────────────────────────────────────
    for (const ss of state.buildSites) {
      const site = this.build.buildSites.find(s => s.id === ss.id);
      if (site) {
        site.gearProgress = ss.gearProgress;
        site.completed    = ss.completed;
      }
    }

    return true;
  }

  /**
   * Find any idle player units sharing a tile and nudge the "lighter" one
   * (the one with the higher unit array index) to the nearest free adjacent tile.
   * Runs once per second — quiet background correction, not per-frame jitter.
   */
  private separateStackedUnits(): void {
    // Check ALL living, non-downed units — not just idle ones.
    // Moving units can simultaneously arrive on the same tile; waiting for them
    // to become idle before separating them means the freeze can persist until
    // Unit.blockedTimer fires (up to 0.35 s later).
    const active = this.units.filter(
      u => u.alive && !u.isDowned && u.specialRole !== 'baddieBeacon',
    );
    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i];
        const b = active[j];
        // Only separate same-faction units — cross-faction crowding is combat,
        // not a stacking bug. Nudging across factions would move band members
        // off venue access tiles when enemies share the tile.
        if (a.faction !== b.faction) continue;
        if (a.gridPos.col !== b.gridPos.col || a.gridPos.row !== b.gridPos.row) continue;
        // Push b to the nearest unoccupied adjacent passable tile.
        const free = this.nearestFreeTile(b.gridPos, b);
        if (free) b.issueMove(this.pathfinding.findPath(b.gridPos, free), free);
      }
    }
  }

  private nearestFreeTile(pos: GridPosition, except: Unit): GridPosition | null {
    const offsets: [number, number][] = [
      [1,0],[-1,0],[0,1],[0,-1],[1,1],[-1,1],[1,-1],[-1,-1],
    ];
    for (const [dc, dr] of offsets) {
      const candidate = { col: pos.col + dc, row: pos.row + dr };
      if (
        this.map.inBounds(candidate.col, candidate.row) &&
        this.map.isPassable(candidate) &&
        !this.isTileOccupied(candidate, except)
      ) return candidate;
    }
    return null;
  }

  /**
   * Returns true if `pos` is already occupied by a living unit other than `except`.
   *
   * `crossFactionPassable` (default false) — when true, units of a DIFFERENT
   * faction from `except` are ignored. Use this during movement so that enemies
   * and player units don't block each other's tile entry: the combat system halts
   * units when they reach attack range, so tile-level exclusion between factions
   * only causes the freeze/shake bug where both sides oscillate at moveT=0.95.
   * Spawn-point and formation helpers leave this false so they still avoid all units.
   */
  private isTileOccupied(
    pos: GridPosition,
    except?: Unit,
    crossFactionPassable = false,
  ): boolean {
    return this.units.some((unit) => {
      if (unit === except || !unit.alive) return false;
      // Cross-faction passability: skip opponents so they don't block each other.
      if (crossFactionPassable && except && unit.faction !== except.faction) return false;
      // Block if the unit is standing on this tile.
      if (unit.gridPos.col === pos.col && unit.gridPos.row === pos.row) return true;
      // Also block if the unit is mid-stride INTO this tile (path[0]).
      // Without this, two same-faction units moving simultaneously can both claim
      // the same tile.
      if (unit.path.length > 0 &&
          unit.path[0].col === pos.col && unit.path[0].row === pos.row) return true;
      return false;
    });
  }

  // ── Move command ─────────────────────────────────────────────────────────

  private issueMoveCommand(screenX: number, screenY: number): void {
    if (this.selection.selected.size === 0) return;

    const world  = this.camera.screenToWorld(screenX, screenY);
    const tile   = this.map.worldToGrid(world);
    if (!this.map.inBounds(tile.col, tile.row)) return;
    const enemy = this.attackableUnitAt(world);
    if (enemy) {
      this.issueAttackTargetCommand(enemy);
      return;
    }

    const selected = Array.from(this.selection.selected);

    // Sort by proximity to target — closest unit gets the centre formation slot
    selected.sort((a, b) => {
      const da = Math.hypot(a.gridPos.col - tile.col, a.gridPos.row - tile.row);
      const db = Math.hypot(b.gridPos.col - tile.col, b.gridPos.row - tile.row);
      return da - db;
    });

    // Request extra candidates so occupied tiles can be skipped without running out.
    const candidates = this.pathfinding.formationTargets(tile, selected.length + 12);
    const reserved   = new Set<string>(); // tiles already claimed by earlier units this batch

    for (const unit of selected) {
      // Pick the first candidate that isn't occupied by a standing/moving unit
      // and hasn't already been reserved for another unit in this move order.
      const dest = candidates.find(c => {
        const key = `${c.col},${c.row}`;
        if (reserved.has(key)) return false;
        if (this.isTileOccupied(c, unit)) return false;
        return true;
      }) ?? tile;

      reserved.add(`${dest.col},${dest.row}`);
      unit.issueMove(this.pathfinding.findPath(unit.gridPos, dest), dest);
    }

    this.showMoveIndicator(tile);
  }

  private issueAttackMoveCommand(screenX: number, screenY: number): void {
    if (this.selection.selected.size === 0) return;

    const world = this.camera.screenToWorld(screenX, screenY);
    const enemy = this.attackableUnitAt(world);
    if (enemy) {
      this.issueAttackTargetCommand(enemy);
      return;
    }

    const tile = this.map.worldToGrid(world);
    if (!this.map.inBounds(tile.col, tile.row)) return;

    const selected = Array.from(this.selection.selected)
      .filter(unit => unit.alive && !unit.isDowned);
    selected.sort((a, b) => {
      const da = Math.hypot(a.gridPos.col - tile.col, a.gridPos.row - tile.row);
      const db = Math.hypot(b.gridPos.col - tile.col, b.gridPos.row - tile.row);
      return da - db;
    });

    const candidates = this.pathfinding.formationTargets(tile, selected.length + 12);
    const reserved   = new Set<string>();

    for (const unit of selected) {
      const dest = candidates.find(c => {
        const key = `${c.col},${c.row}`;
        if (reserved.has(key)) return false;
        if (this.isTileOccupied(c, unit)) return false;
        return true;
      }) ?? tile;

      reserved.add(`${dest.col},${dest.row}`);
      unit.issueAttackMove(this.pathfinding.findPath(unit.gridPos, dest), tile);
    }

    this.showMoveIndicator(tile, 0xff8833);
  }

  private issueAttackTargetCommand(target: Unit): void {
    if (target.enemyType === 'MusicPress' && target.faction === Faction.Neutral) {
      const promoters = Array.from(this.selection.selected).filter(unit =>
        unit.alive && !unit.isDowned && unit.bandClass === 'promoter',
      );
      if (promoters.length === 0) {
        this.toasts.show('Send Ali & The Vocal Mob to win over the Music Press', 'info', 3.5);
        return;
      }
      for (const promoter of promoters) promoter.issueAttackTarget(target);
      this.showMoveIndicator(target.gridPos, 0x37e0d0);
      return;
    }

    for (const unit of this.selection.selected) {
      if (!unit.alive || unit.isDowned) continue;
      unit.issueAttackTarget(target);
    }
    this.showMoveIndicator(target.gridPos, 0xff8833);
  }

  private attackableUnitAt(world: { x: number; y: number }): Unit | null {
    let closest: Unit | null = null;
    let closestDist = UNIT_HIT_RADIUS;
    for (const unit of this.units) {
      if (!unit.alive || unit.isDowned || unit.faction === Faction.Player || unit.enemyType === null) continue;
      const positions = [this.map.tileTopCenter(unit.gridPos.col, unit.gridPos.row)];
      if (unit.path.length > 0) {
        const nextCenter = this.map.tileTopCenter(unit.path[0].col, unit.path[0].row);
        positions.push(nextCenter);
        if (unit.moveT > 0) {
          const gridCenter = positions[0];
          positions.push({
            x: gridCenter.x + (nextCenter.x - gridCenter.x) * unit.moveT,
            y: gridCenter.y + (nextCenter.y - gridCenter.y) * unit.moveT,
          });
        }
      }
      for (const pos of positions) {
        const dist = Math.hypot(world.x - pos.x, world.y - pos.y);
        if (dist < closestDist) {
          closest = unit;
          closestDist = dist;
        }
      }
    }
    return closest;
  }

  // ── Move indicator ────────────────────────────────────────────────────────

  private showTorrentShot(from: GridPosition, to: GridPosition): void {
    const fromWp = this.map.tileTopCenter(from.col, from.row);
    const toWp   = this.map.tileTopCenter(to.col, to.row);
    const g = new Graphics();
    this.world.addChild(g);
    this.torrentShots.push({
      g,
      from: { x: fromWp.x, y: fromWp.y - 18 },
      to: { x: toWp.x, y: toWp.y - 14 },
      elapsed: 0,
      lifetime: TORRENT_SHOT_LIFETIME,
    });
  }

  private updateTorrentShots(delta: number): void {
    this.torrentShots = this.torrentShots.filter(shot => {
      shot.elapsed += delta;
      if (shot.elapsed >= shot.lifetime) {
        this.world.removeChild(shot.g);
        shot.g.destroy();
        return false;
      }

      const t = shot.elapsed / shot.lifetime;
      const alpha = Math.sin(t * Math.PI);
      const dx = shot.to.x - shot.from.x;
      const dy = shot.to.y - shot.from.y;
      const length = Math.max(1, Math.hypot(dx, dy));
      const nx = -dy / length;
      const ny = dx / length;
      const headX = shot.from.x + dx * t;
      const headY = shot.from.y + dy * t;

      shot.g.clear();
      shot.g.lineStyle(2.5, 0x37e0d0, alpha * 0.85);
      shot.g.moveTo(shot.from.x + dx * Math.max(0, t - 0.28), shot.from.y + dy * Math.max(0, t - 0.28));
      shot.g.lineTo(headX, headY);
      shot.g.lineStyle(1.2, 0xffdd55, alpha * 0.75);
      shot.g.moveTo(shot.from.x + dx * Math.max(0, t - 0.16), shot.from.y + dy * Math.max(0, t - 0.16));
      shot.g.lineTo(headX, headY);

      for (let i = 0; i < 4; i++) {
        const packetT = Math.max(0, Math.min(1, t - i * 0.055));
        const px = shot.from.x + dx * packetT + nx * (i % 2 === 0 ? 4 : -4);
        const py = shot.from.y + dy * packetT + ny * (i % 2 === 0 ? 4 : -4);
        const size = 4 + (1 - i / 4) * 3;
        shot.g.beginFill(i === 0 ? 0xffdd55 : 0x37e0d0, alpha * (0.8 - i * 0.12));
        shot.g.drawRect(px - size / 2, py - size / 2, size, size);
        shot.g.endFill();
      }

      return true;
    });
  }

  private showMoveIndicator(tile: GridPosition, colour = 0x00ffff): void {
    const wp = this.map.tileTopCenter(tile.col, tile.row);
    const g  = new Graphics();
    g.position.set(wp.x, wp.y);
    this.world.addChild(g);
    this.moveIndicators.push({ g, elapsed: 0, lifetime: INDICATOR_LIFETIME, colour });
  }

  private updateIndicators(delta: number): void {
    this.moveIndicators = this.moveIndicators.filter(ind => {
      ind.elapsed += delta;
      if (ind.elapsed >= ind.lifetime) {
        this.world.removeChild(ind.g);
        ind.g.destroy();
        return false;
      }
      const t     = ind.elapsed / ind.lifetime;
      const alpha = 1 - t;
      const scale = 0.4 + t * 0.6; // expands outward as it fades

      ind.g.clear();
      ind.g.lineStyle(1.5, ind.colour, alpha);
      ind.g.moveTo(0, -HH * scale);
      ind.g.lineTo(HW * scale, 0);
      ind.g.lineTo(0,  HH * scale);
      ind.g.lineTo(-HW * scale, 0);
      ind.g.closePath();
      return true;
    });
  }

  // ── Input ─────────────────────────────────────────────────────────────────

  private bindInput(): void {
    const view = this.app.view as HTMLCanvasElement;

    // Keyboard
    window.addEventListener('keydown', (e) => {
      const k = e.key.toLowerCase();
      if (k.startsWith('arrow')) e.preventDefault();

      // Backtick — toggle debug overlay visibility
      if (k === '`') {
        this.toggleOverlay();
        e.preventDefault();
        return;
      }

      // Onboarding: space/enter advances, takes priority over all other keys
      if (this.onboarding?.handleKey(e.key)) {
        e.preventDefault();
        return;
      }

      // B — toggle build menu
      if (this.gameState === 'playing' && k === 'b') {
        if (this.placement.placementType !== null) {
          this.placement.cancelPlacement();
        } else {
          this.buildMenu.toggle();
        }
        e.preventDefault();
        return;
      }

      // Number keys 1-4 during build menu — select building
      if (this.gameState === 'playing' && this.buildMenu.isVisible && k >= '1' && k <= '4') {
        const idx = parseInt(k) - 1;
        if (idx < BUILDING_DEFS.length) {
          this.buildMenu.hide();
          this.placement.enterPlacementMode(BUILDING_DEFS[idx].type);
        }
        e.preventDefault();
        return;
      }

      if (this.gameState === 'playing' && k === 'a' && this.selection.selected.size > 0) {
        this.attackCommandArmed = true;
        this.toasts.show('Attack order armed', 'info', 1.4);
        e.preventDefault();
        return;
      }

      // Command panel hotkeys — consume before other handling
      if (this.gameState === 'playing' && this.commandPanel.handleKey(k)) return;

      this.keys.add(k);

      if (k === 'escape') {
        if (this.placement.placementType !== null) {
          this.placement.cancelPlacement();
        } else if (this.buildMenu.isVisible) {
          this.buildMenu.hide();
        } else if (this.gameState === 'playing') {
          this.gameState = 'paused';
          this.pauseMenu.show(this.app.screen.width, this.app.screen.height);
        } else if (this.gameState === 'paused') {
          this.gameState = 'playing';
          this.pauseMenu.hide();
        }
      }
    });
    window.addEventListener('keyup',  (e) => this.keys.delete(e.key.toLowerCase()));
    window.addEventListener('blur',   ()  => this.keys.clear());

    // Pointer move
    view.addEventListener('pointermove', (e) => {
      this.pointer.x      = e.offsetX;
      this.pointer.y      = e.offsetY;
      this.pointer.inside = true;

      if ((this.mbDragging || this.rbDragging) && !this.cinemaZoom) {
        this.camera.pan(-e.movementX, -e.movementY);
      }

      // Touch: single-finger pan  /  two-finger pinch-zoom + pan
      if (e.pointerType === 'touch' && this.touchPointers.has(e.pointerId)) {
        const prev = this.touchPointers.get(e.pointerId)!;
        this.touchPointers.set(e.pointerId, { x: e.offsetX, y: e.offsetY });

        if (this.touchPointers.size === 2) {
          const pts  = [...this.touchPointers.values()];
          const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          const midX = (pts[0].x + pts[1].x) / 2;
          const midY = (pts[0].y + pts[1].y) / 2;
          if (this.touchPinchDist > 0 && !this.cinemaZoom) {
            this.camera.zoomAt(dist / this.touchPinchDist, midX, midY);
          }
          if (!this.cinemaZoom) {
            this.camera.pan(-(midX - this.touchMidpoint.x), -(midY - this.touchMidpoint.y));
          }
          this.touchPinchDist = dist;
          this.touchMidpoint  = { x: midX, y: midY };
          this.touchMoved = true;
        } else if (this.touchPointers.size === 1) {
          const dx = e.offsetX - prev.x;
          const dy = e.offsetY - prev.y;
          if (!this.touchMoved && Math.hypot(dx, dy) > 8) this.touchMoved = true;
          if (this.touchMoved) this.camera.pan(-dx, -dy);
        }
      }

      // Track right-button drag
      if (this.rbDown && this.rbDragStart && !this.rbDragging) {
        const dx = e.offsetX - this.rbDragStart.sx;
        const dy = e.offsetY - this.rbDragStart.sy;
        if (Math.hypot(dx, dy) > 5) {
          this.rbMoved    = true;
          this.rbDragging = true;
        }
      }

      // Track left-button drag
      if (this.lbDown && this.lbDragStart) {
        const dx = e.offsetX - this.lbDragStart.sx;
        const dy = e.offsetY - this.lbDragStart.sy;
        if (Math.hypot(dx, dy) > 4) this.lbMoved = true;
        if (this.lbMoved) this.drawSelectionBox(e.offsetX, e.offsetY);
      }

      this.updateHover();

      // Stage 8: update placement ghost tile
      if (this.placement.placementType !== null && this.hoverTile) {
        this.placement.updateGhostTile(this.hoverTile.col, this.hoverTile.row);
      } else {
        this.placement.updateGhostTile(null, null);
      }
    });

    view.addEventListener('pointerleave', () => {
      this.pointer.inside = false;
      this.hoverTile      = null;
      this.mapRenderer.setHover(null);
      this.placement.updateGhostTile(null, null);
    });

    // Pointer down
    view.addEventListener('pointerdown', (e) => {
      // Ignore game-world clicks when on menu or paused
      if (this.gameState !== 'playing') return;

      const inPanel = e.offsetY > this.app.screen.height - this.commandPanel.panelHeight;

      if (e.button === 0 && !inPanel) {
        if (e.pointerType === 'touch') {
          this.touchPointers.set(e.pointerId, { x: e.offsetX, y: e.offsetY });
          view.setPointerCapture(e.pointerId);
          if (this.touchPointers.size === 2) {
            const pts = [...this.touchPointers.values()];
            this.touchPinchDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
            this.touchMidpoint  = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
            this.touchMoved = true; // second finger cancels any pending tap
          }
        } else {
          this.lbDown      = true;
          this.lbDragStart = { sx: e.offsetX, sy: e.offsetY };
          this.lbMoved     = false;
          view.setPointerCapture(e.pointerId);
        }
      }
      if (e.button === 1) {
        this.mbDragging = true;
        view.setPointerCapture(e.pointerId);
        e.preventDefault();
      }
      if (e.button === 2 && !inPanel) {
        this.rbDown      = true;
        this.rbDragStart = { sx: e.offsetX, sy: e.offsetY };
        this.rbMoved     = false;
        this.rbDragging  = false;
        view.setPointerCapture(e.pointerId);
        e.preventDefault();
      }
    });

    // Pointer up
    view.addEventListener('pointerup', (e) => {
      // Touch lift — tap-to-select or end of pan/pinch
      if (e.pointerType === 'touch' && this.touchPointers.has(e.pointerId)) {
        const wasTap = !this.touchMoved && this.touchPointers.size === 1;
        this.touchPointers.delete(e.pointerId);
        view.releasePointerCapture(e.pointerId);
        if (this.touchPointers.size < 2) this.touchPinchDist = 0;
        if (this.touchPointers.size === 0) this.touchMoved = false;
        if (wasTap && this.gameState === 'playing') {
          const inPanel = e.offsetY > this.app.screen.height - this.commandPanel.panelHeight;
          if (!inPanel) {
            if (this.selection.selected.size > 0 && !this.selection.hasPlayerUnitAt(e.offsetX, e.offsetY)) {
              // Tap on empty tile or enemy with active selection → move / attack-move
              this.issueMoveCommand(e.offsetX, e.offsetY);
            } else if (this.selection.selectAt(e.offsetX, e.offsetY, false)) {
              this.updatePanel();
            }
          }
        }
      }

      // Left button — select (or place building in placement mode)
      if (e.button === 0 && this.lbDown) {
        this.lbDown = false;

        // Stage 8: placement mode — left click places the building
        if (this.placement.placementType !== null && !this.lbMoved && this.lbDragStart) {
          const world = this.camera.screenToWorld(e.offsetX, e.offsetY);
          const tile  = this.map.worldToGrid(world);
          const venueOccupied = (c: number, r: number) =>
            this.build.venues.some(v => v.buildingCol === c && v.buildingRow === r);
          const lastType = this.placement.placementType;
          const placed = this.placement.tryPlace(tile.col, tile.row, venueOccupied);
          if (placed) {
            const bs = this.placement.buildings;
            const lastBuilt = bs.length > 0 ? bs[bs.length - 1] : null;
            const def = BUILDING_DEFS.find(d => d.type === lastBuilt?.type || d.type === lastType);
            this.toasts.show(`${def?.name ?? 'Building'} placed!`, 'success', 2.0);
          } else {
            this.toasts.show('Cannot build here', 'warning', 1.5);
          }
          this.lbDragStart = null;
          this.lbMoved = false;
          view.releasePointerCapture(e.pointerId);
          return;
        }

        if (this.attackCommandArmed && !this.lbMoved) {
          this.issueAttackMoveCommand(e.offsetX, e.offsetY);
          this.attackCommandArmed = false;
        } else if (!this.lbMoved && this.lbDragStart) {
          if (this.selection.selectAt(e.offsetX, e.offsetY, e.shiftKey)) this.updatePanel();
        } else if (this.lbMoved && this.lbDragStart) {
          this.selection.boxSelect(
            { x: this.lbDragStart.sx, y: this.lbDragStart.sy },
            { x: e.offsetX,           y: e.offsetY           },
          );
          this.updatePanel();
        }
        this.lbDragStart = null;
        this.lbMoved     = false;
        this.selBoxGfx.clear();
        view.releasePointerCapture(e.pointerId);
      }

      // Middle button — stop pan
      if (e.button === 1 && this.mbDragging) {
        this.mbDragging = false;
        view.releasePointerCapture(e.pointerId);
      }

      // Right button — move command OR pan release
      if (e.button === 2 && this.rbDown) {
        this.rbDown = false;
        if (!this.rbMoved) {
          if (this.attackCommandArmed) {
            this.issueAttackMoveCommand(e.offsetX, e.offsetY);
            this.attackCommandArmed = false;
          } else {
            // Short click — direct attack if an enemy is under the cursor,
            // otherwise plain movement.
            this.issueMoveCommand(e.offsetX, e.offsetY);
          }
        }
        this.rbDragStart = null;
        this.rbMoved     = false;
        this.rbDragging  = false;
        view.releasePointerCapture(e.pointerId);
      }
    });

    view.addEventListener('contextmenu', (e) => e.preventDefault());

    // Scroll zoom (suppressed during cinematic)
    view.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (this.cinemaZoom) return;
      const factor = e.deltaY < 0 ? CAMERA_ZOOM_STEP : 1 / CAMERA_ZOOM_STEP;
      this.camera.zoomAt(factor, e.offsetX, e.offsetY);
      this.updateHover();
    }, { passive: false });

    // Selection box (UI layer — screen-space)
    this.selBoxGfx = new Graphics();
    this.ui.addChild(this.selBoxGfx);
  }

  private drawSelectionBox(curX: number, curY: number): void {
    if (!this.lbDragStart) return;
    const { sx, sy } = this.lbDragStart;
    this.selBoxGfx.clear();
    this.selBoxGfx.lineStyle(1, 0x00ffff, 0.8);
    this.selBoxGfx.beginFill(0x00ffff, 0.06);
    this.selBoxGfx.drawRect(
      Math.min(sx, curX), Math.min(sy, curY),
      Math.abs(curX - sx), Math.abs(curY - sy),
    );
    this.selBoxGfx.endFill();
  }

  private updateHover(): void {
    const world    = this.camera.screenToWorld(this.pointer.x, this.pointer.y);
    const tile     = this.map.worldToGrid(world);
    this.hoverTile = this.map.inBounds(tile.col, tile.row) ? tile : null;
    this.mapRenderer.setHover(this.hoverTile);
  }

  // ── Panel ────────────────────────────────────────────────────────────────

  private updatePanel(): void {
    this.commandPanel.show(Array.from(this.selection.selected));
  }

  // ── Overlay ──────────────────────────────────────────────────────────────

  private buildOverlay(): void {
    const monoStyle = (size: number, colour: string) => new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: size,
      fill: colour,
      lineHeight: size + 5,
    });

    // Persistent header: title + buttons (always visible)
    this.overlay = new Text('FUTURE BEACON · debug', monoStyle(13, '#c9fff8'));
    this.overlay.position.set(12, 10);

    this.overlayToggleBtn = new Text('[ HIDE ]', monoStyle(12, '#37e0d0'));
    this.overlayToggleBtn.position.set(0, 10); // x set in updateOverlay
    this.overlayToggleBtn.eventMode = 'static';
    this.overlayToggleBtn.cursor = 'pointer';
    this.overlayToggleBtn.on('pointerdown', () => this.toggleOverlay());

    this.overlayAutoplayBtn = new Text('[ AUTOPLAY: OFF ]', monoStyle(12, '#888'));
    this.overlayAutoplayBtn.position.set(0, 10); // x set in updateOverlay
    this.overlayAutoplayBtn.eventMode = 'static';
    this.overlayAutoplayBtn.cursor = 'pointer';
    this.overlayAutoplayBtn.on('pointerdown', () => this.toggleAutoplay());

    // Body lines — hidden when overlay is collapsed
    this.overlayBody = new Text('', monoStyle(12, '#8ecfc8'));
    this.overlayBody.position.set(12, 30);
    this.overlayBody.visible = this.overlayVisible;

    this.hint = new Text(
      'WASD/arrows · scroll zoom · select · right-click move/attack · A attack-move · ESC pause',
      new TextStyle({ fontFamily: 'Menlo, Consolas, monospace', fontSize: 11, fill: '#628e82' }),
    );

    this.versionStamp = new Text(APP_RELEASE_STAMP, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   10,
      fill:       '#3a5550',
    }));
    this.versionStamp.anchor.set(1, 1);

    this.aboutLink = new Text('About Future Beacon', new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   10,
      fill:       '#507a70',
    }));
    this.aboutLink.anchor.set(1, 1);
    this.aboutLink.eventMode = 'static';
    this.aboutLink.cursor    = 'pointer';
    this.aboutLink.on('pointerdown', () => window.open('./about.html', '_blank'));
    this.aboutLink.on('pointerover', () => { (this.aboutLink.style as TextStyle).fill = '#00FFCC'; });
    this.aboutLink.on('pointerout',  () => { (this.aboutLink.style as TextStyle).fill = '#507a70'; });

    this.ui.addChild(
      this.overlay, this.overlayToggleBtn, this.overlayAutoplayBtn,
      this.overlayBody, this.hint, this.versionStamp, this.aboutLink,
    );
    this.positionHint();
  }

  private toggleOverlay(): void {
    this.overlayVisible = !this.overlayVisible;
    this.overlayBody.visible = this.overlayVisible;
    this.updateOverlayButtons();
  }

  private toggleAutoplay(): void {
    this.autoplayActive = !this.autoplayActive;
    this.autoplayTick   = this.AUTOPLAY_INTERVAL; // fire immediately on next tick
    this.updateOverlayButtons();
    if (this.autoplayActive) {
      this.toasts.show('◈ Autoplay active — Electrasy vs Industry!', 'success', 4.0);
      trackBattleEvent('autoplay_toggled', { active: true, wave: this.waves.currentWaveIndex, elapsed: this.elapsed });
    } else {
      this.toasts.show('Autoplay off', 'info', 2.0);
      trackBattleEvent('autoplay_toggled', { active: false, wave: this.waves.currentWaveIndex, elapsed: this.elapsed });
    }
  }

  private updateOverlayButtons(): void {
    // [HIDE]/[SHOW] button sits immediately after the title text
    const titleRight = 12 + this.overlay.width + 10;
    this.overlayToggleBtn.text = this.overlayVisible ? '[ HIDE ]' : '[ SHOW ]';
    this.overlayToggleBtn.style.fill = '#37e0d0';
    this.overlayToggleBtn.position.set(titleRight, 10);

    // [AUTOPLAY] button sits after the toggle
    const toggleRight = titleRight + this.overlayToggleBtn.width + 10;
    this.overlayAutoplayBtn.text = this.autoplayActive ? '[ AUTOPLAY: ON ]' : '[ AUTOPLAY: OFF ]';
    this.overlayAutoplayBtn.style.fill = this.autoplayActive ? '#44cc66' : '#666';
    this.overlayAutoplayBtn.position.set(toggleRight, 10);
  }

  private updateOverlay(): void {
    this.updateOverlayButtons();

    if (!this.overlayVisible) return;

    const tl  = this.camera.topLeft;
    const sel = this.selection.selected.size;

    let hoverLine = this.attackCommandArmed ? 'ATTACK ORDER ARMED — click enemy or ground' : 'hover: —';
    if (this.hoverTile) {
      const name = this.map.isBeacon(this.hoverTile.col, this.hoverTile.row)
        ? 'beacon'
        : this.map.getTerrain(this.hoverTile);
      hoverLine = this.attackCommandArmed
        ? `ATTACK ORDER ARMED — (${this.hoverTile.col}, ${this.hoverTile.row}) ${name}`
        : `hover: (${this.hoverTile.col}, ${this.hoverTile.row}) ${name}`;
    }

    const enemies = this.units.filter(u => u.faction !== Faction.Player).length;
    const wave    = this.waves.currentWaveIndex;

    this.overlayBody.text = [
      `cam: (${Math.round(tl.x)}, ${Math.round(tl.y)})  zoom: ${this.camera.currentZoom.toFixed(2)}x`,
      hoverLine,
      `fps: ${Math.round(this.fps)}   tempo: ${this.rhythm.tempoModifier.toFixed(2)}×   selected: ${sel}   enemies: ${enemies}   wave: ${wave}`,
      this.autoplayActive ? `autoplay: ${this.autoplayIntent}` : 'autoplay: off',
    ].join('\n');
  }

  private positionHint(): void {
    const W = this.app.screen.width;
    const H = this.app.screen.height;
    const isMobile = W < 600;
    const footerPad = isMobile ? 8 : 12;
    const footerGap = isMobile ? 5 : 4;

    this.versionStamp.style.fontSize = isMobile ? 9 : 10;
    this.aboutLink.style.fontSize = isMobile ? 9 : 10;
    this.hint.style.fontSize = isMobile ? 10 : 11;

    if (isMobile) {
      this.hint.text = 'tap: select · right-tap: move · pinch: zoom · ESC: pause';
    } else {
      this.hint.text =
        'WASD/arrows · scroll zoom · select · right-click move/attack · A attack-move · ESC pause';
    }

    this.hint.position.set(12, H - 24);
    this.versionStamp.visible = this.gameFooterVisible;
    this.versionStamp.position.set(W - footerPad, H - footerPad);
    this.aboutLink.visible = this.gameFooterVisible && !isMobile;
    if (this.aboutLink.visible) {
      // Use fontSize as height proxy — versionStamp.height is 0 before first render.
      const stampLineH = (isMobile ? 9 : 10) + 3;
      this.aboutLink.position.set(
        W - footerPad,
        H - footerPad - stampLineH - footerGap,
      );
    }
  }

  private setGameFooterVisible(visible: boolean): void {
    this.gameFooterVisible = visible;
    if (this.versionStamp && this.aboutLink) this.positionHint();
  }

  private onResize(): void {
    if (!this.camera) return;
    const W = this.app.screen.width, H = this.app.screen.height;
    this.camera.setViewport(W, H);
    this.fogRenderer?.setScreenSize(W, H);
    this.commandPanel.resize(W, H);
    this.hud.resize(W, H);
    this.toasts?.resize(W, H);
    this.mainMenu?.resize(W, H);
    this.pauseMenu?.resize(W, H);
    this.gameOverScreen?.resize(W, H);
    this.onboarding?.resize(W, H);
    this.introScreen?.resize(W, H);
    this.buildMenu?.resize(W, H);
    this.positionHint();
  }

  // ── Command button definitions ───────────────────────────────────────────
  //
  // All lambdas close over `this` — game systems are accessed at call time, so
  // it's safe to define these buttons before all systems are initialised.

  private makeButtons(): CommandButton[] {
    return [
      {
        label:   'STOP',
        hotkey:  'x',
        sub:     'all units',
        colour:  0x8aa39f,
        visible: () => true,
        enabled: (units) => units.some(u => u.isMoving && !u.isDowned),
        execute: (units) => {
          units.forEach((u) => {
            u.setPath([]);
            u.clearCombatOrder();
          });
        },
      },
      {
        label:   'ATTACK',
        hotkey:  'a',
        sub:     'next click',
        colour:  0xff8833,
        visible: () => true,
        enabled: (units) => units.some(u => u.alive && !u.isDowned),
        execute: () => {
          this.attackCommandArmed = true;
          this.toasts.show('Attack order armed', 'info', 1.4);
        },
      },
      {
        label:   'MINE GEAR',
        hotkey:  'g',
        sub:     'fans only',
        colour:  0xffd700,
        visible: (units) => units.some(u => u.bandClass === 'fan' && !u.isDowned),
        enabled: (units) => units.some(u => u.bandClass === 'fan' && u.alive && !u.isDowned),
        execute: (units) => {
          for (const fan of units.filter(u => u.bandClass === 'fan' && u.alive && !u.isDowned)) {
            const nearest = this.gearSystem.nodes
              .map(n => ({ n, d: Math.hypot(fan.gridPos.col - n.col, fan.gridPos.row - n.row) }))
              .sort((a, b) => a.d - b.d)[0]?.n;
            if (nearest) {
              const dest = { col: nearest.col, row: nearest.row };
              fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
            }
          }
        },
      },
      {
        label:   'CLAIM',
        hotkey:  'c',
        sub:     'nearest venue',
        colour:  0x44cc66,
        visible: (units) => !units.some(u => u.bandClass === 'mystic' && u.alive && !u.isDowned),
        enabled: (units) =>
          units.some(u => u.alive && !u.isDowned) &&
          this.build.venues.some(v => v.state === 'unclaimed'),
        execute: (units) => {
          // Track which venue access tiles are already en-route targets (this batch + pre-existing).
          // keyed as "col,row" matching venue access coords.
          const takenThisBatch = new Set<string>();

          for (const unit of units.filter(u => u.alive && !u.isDowned)) {
            // Collect destinations already claimed by ALL other living player units.
            const takenByOthers = new Set<string>();
            for (const other of this.units) {
              if (other === unit || !other.alive || other.faction !== Faction.Player) continue;
              if (other.orderTarget) {
                takenByOthers.add(`${other.orderTarget.col},${other.orderTarget.row}`);
              }
            }

            const nearest = this.build.venues
              .filter(v => {
                if (v.state !== 'unclaimed') return false;
                const key = `${v.col},${v.row}`;
                return !takenByOthers.has(key) && !takenThisBatch.has(key);
              })
              .map(v => ({ v, d: Math.hypot(unit.gridPos.col - v.col, unit.gridPos.row - v.row) }))
              .sort((a, b) => a.d - b.d)[0]?.v;

            if (nearest) {
              const dest = { col: nearest.col, row: nearest.row };
              takenThisBatch.add(`${dest.col},${dest.row}`);
              unit.issueMove(this.pathfinding.findPath(unit.gridPos, dest), dest);
            }
          }
        },
      },
      {
        label:   'ASSIST',
        hotkey:  'f',
        sub:     'fans → claim',
        colour:  0xff8833,
        visible: (units) => units.some(u => u.bandClass === 'fan' && !u.isDowned),
        enabled: (units) =>
          units.some(u => u.bandClass === 'fan' && u.alive && !u.isDowned) &&
          this.build.venues.some(v => v.state === 'claiming'),
        execute: (units) => {
          for (const fan of units.filter(u => u.bandClass === 'fan' && u.alive && !u.isDowned)) {
            const nearest = this.build.venues
              .filter(v => v.state === 'claiming')
              .map(v => ({ v, d: Math.hypot(fan.gridPos.col - v.col, fan.gridPos.row - v.row) }))
              .sort((a, b) => a.d - b.d)[0]?.v;
            if (nearest) {
              const dest = { col: nearest.col, row: nearest.row };
              fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
            }
          }
        },
      },
      {
        label:   'UPGRADE',
        hotkey:  'u',
        sub: (units) => {
          const venue = this.nearestClaimedVenueForUnits(units);
          if (!venue) return undefined;
          if (venue.venueLevel >= 3) return 'MAX';
          const nextLevel = venue.venueLevel + 1;
          const progress = this.resources.progression;
          if (nextLevel === 2 && !progress.canUpgradeLevel2) return 'LOCKED: build buzz';
          if (nextLevel === 3 && !progress.canUpgradeLevel3) return 'LOCKED: breakout';
          const rep   = nextLevel === 2 ? VENUE_UPGRADE_L2_REP   : VENUE_UPGRADE_L3_REP;
          const sales = nextLevel === 2 ? VENUE_UPGRADE_L2_SALES : VENUE_UPGRADE_L3_SALES;
          return `L${nextLevel}: ${rep}R ${sales}S`;
        },
        colour:  0x66aaff,
        visible: (units) =>
          units.some(u => u.alive && !u.isDowned) &&
          this.build.venues.some(v => v.state === 'claimed' && v.venueLevel < 3),
        enabled: (units) => {
          const venue = this.nearestClaimedVenueForUnits(units);
          if (!venue || venue.venueLevel >= 3) return false;
          const nextLevel = venue.venueLevel + 1;
          const progress = this.resources.progression;
          if (nextLevel === 2 && !progress.canUpgradeLevel2) return false;
          if (nextLevel === 3 && !progress.canUpgradeLevel3) return false;
          const rep   = nextLevel === 2 ? VENUE_UPGRADE_L2_REP   : VENUE_UPGRADE_L3_REP;
          const sales = nextLevel === 2 ? VENUE_UPGRADE_L2_SALES : VENUE_UPGRADE_L3_SALES;
          return this.resources.canAfford({ reputation: rep, sales: sales });
        },
        execute: (units) => {
          const venue = this.nearestClaimedVenueForUnits(units);
          if (!venue) return;
          const result = this.build.upgradeVenue(venue.id);
          if (result.success) {
            const mult  = venue.venueLevel === 2 ? '1.6' : '2.6';
            this.toasts.show(`◈ ${venue.name} upgraded to Level ${venue.venueLevel}! (×${mult} income)`, 'success', 4.0);
            this.commandPanel.rebuild();
          } else if (result.reason === 'cannot afford') {
            const nextLevel = venue.venueLevel + 1;
            const rep   = nextLevel === 2 ? VENUE_UPGRADE_L2_REP   : VENUE_UPGRADE_L3_REP;
            const sales = nextLevel === 2 ? VENUE_UPGRADE_L2_SALES : VENUE_UPGRADE_L3_SALES;
            this.toasts.show(`${venue.name} upgrade needs ${rep} REP + ${sales} SALES`, 'warning', 3.0);
          } else if (result.reason) {
            this.toasts.show(result.reason, 'warning', 4.0);
          }
        },
      },
      {
        label:   'BUILD',
        hotkey:  'b',
        sub:     () => this.resources.progression.canBuildExpansion ? 'fan workshop' : 'LOCKED: grow FAN',
        colour:  0xba68c8,
        visible: (units) => units.some(u => u.bandClass === 'fan' && !u.isDowned),
        enabled: (units) =>
          units.some(u => u.bandClass === 'fan' && u.alive && !u.isDowned) &&
          this.build.buildSites.some(s => !s.completed) &&
          this.resources.gear > 0 &&
          this.resources.progression.canBuildExpansion,
        execute: (units) => {
          for (const fan of units.filter(u => u.bandClass === 'fan' && u.alive && !u.isDowned)) {
            const nearest = this.build.buildSites
              .filter(s => !s.completed)
              .map(s => ({ s, d: Math.hypot(fan.gridPos.col - s.col, fan.gridPos.row - s.row) }))
              .sort((a, b) => a.d - b.d)[0]?.s;
            if (nearest) {
              const dest = { col: nearest.col, row: nearest.row };
              fan.issueMove(this.pathfinding.findPath(fan.gridPos, dest), dest);
            }
          }
        },
      },
      {
        // ── Active skill — one per band class (not fans/source) ──────────────
        label:   'SKILL',
        hotkey:  'q',
        colour:  0x9955ee,
        // Sub-label: show skill name when ready, countdown when on cooldown
        sub: (units) => {
          const u = units.find(u =>
            u.faction === Faction.Player &&
            u.bandClass && u.bandClass !== 'fan' && u.bandClass !== 'source' &&
            u.alive && !u.isDowned,
          );
          if (!u || !u.bandClass) return undefined;
          if (u.skillCooldown > 0) return `${Math.ceil(u.skillCooldown)}s`;
          return SKILL_NAMES[u.bandClass] ?? 'ready';
        },
        visible: (units) => units.some(
          u => u.faction === Faction.Player &&
               u.bandClass && u.bandClass !== 'fan' && u.bandClass !== 'source' && u.bandClass !== 'mystic',
        ),
        enabled: (units) => units.some(u => canUseSkill(u)),
        execute: (units) => {
          for (const u of units) {
            if (canUseSkill(u)) this.issueSkill(u);
          }
        },
      },

      // ── Jim's six spells (visible only when a mystic unit is selected) ────
      ...SPELLS.map((def): CommandButton => ({
        label:   def.name.toUpperCase().split(' ')[0],
        hotkey:  '',
        colour:  def.pulse,
        sub: () => {
          if (this.spells.isLocked) return `LOCKED ${Math.ceil(this.spells.lockoutSecsRemaining)}s`;
          const cd = this.spells.cooldownFor(def.key);
          return cd > 0 ? `${Math.ceil(cd)}s` : def.desc;
        },
        visible: (units) => units.some(
          u => u.faction === Faction.Player && u.bandClass === 'mystic' && u.alive && !u.isDowned,
        ),
        enabled: () => this.spells.canCast(def.key),
        execute: () => {
          const jim = this.units.find(u =>
            u.faction === Faction.Player && u.bandClass === 'mystic' && u.alive && !u.isDowned,
          );
          if (!jim) return;
          this.spells.cast(def.key, {
            caster:       jim,
            allUnits:     this.units,
            beacon:       this.beaconSystem,
            resources:    this.resources,
            nextWaveInfo: () => this.waves.nextWavePreview(),
            triggerPulse: (colour, duration) => this.mapRenderer.triggerPulse(colour, duration),
            showToast:    (msg, sev) => this.toasts.show(msg, sev, 3.5),
          });
          this.commandPanel.rebuild();
        },
      })),
    ];
  }

  /** Fire a band member's active skill and show a toast. */
  private issueSkill(unit: Unit): void {
    const beaconPos = { col: this.map.beacon.col, row: this.map.beacon.row };
    const playerVenuePositions = this.build.venues
      .filter(v => v.state === 'claimed')
      .map(v => ({ col: v.col, row: v.row }));

    const fired = activateSkill(unit, {
      allUnits:             this.units,
      fogSystem:            this.fog,
      triggerPulse:         (colour, duration) => this.mapRenderer.triggerPulse(colour, duration),
      beaconPos,
      resources:            this.resources,
      playerVenuePositions,
      showToast:            (msg, sev) => this.toasts.show(msg, sev, 2.8),
    });
    if (fired && unit.bandClass) {
      const name = SKILL_NAMES[unit.bandClass] ?? 'Skill';
      this.toasts.show(`${unit.displayName}: ${name}!`, 'info', 2.2);
      this.commandPanel.rebuild();
    }
  }

  get pixi(): Application { return this.app; }
}
